// Componente CreateASN con auto translate aplicado
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import SideMenu from './SideMenu';
import Breadcrumbs from './Breadcrumbs';
import { parseToNumber, formatNumberWithComma, rightAlignedNumberStyle, formattedNumberCell } from '../utils/validator';
import { 
  Wizard,
  WizardStep,
  Title,
  Button,
  Form,
  Input,
  DatePicker,
  Select,
  Option,
  TextArea,
  Label,
  ObjectStatus,
  AnalyticalTable,
  Text,
  MessageBox,
  Dialog,
  BusyIndicator
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import ImportBultos from './ImportBultos';
import ExportExcelButton from './ExportExcelButton';
import ImportExcelButton from './ImportExcelButton';
import useAutoTranslate from '../hooks/useAutoTranslate'; // Importar el hook
import useBlobUploader from '../utils/blobUploader'; // Import the blob uploader hook

const CreateASN = () => {
  const navigate = useNavigate();
  const { autoT } = useAutoTranslate(); // Usar el hook de traducción
  const { uploadFile } = useBlobUploader(); // Initialize the blob uploader hook
  
  const { state } = useLocation();
    
  const [selectedStep, setSelectedStep] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState(state?.purchaseOrder || null);
  const [items, setItems] = useState(state?.items || []);
  const [positions, setPositions] = useState([]);
  const [bultos, setBultos] = useState([]);
  const [attachments, setAttachments] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [cantidadBultos, setCantidadBultos] = useState(0);
  
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shipmentDate, setShipmentDate] = useState('');
  const [transportType, setTransportType] = useState('');
  const [shipmentType, setShipmentType] = useState('');
  const [cargoType, setCargoType] = useState('');
  const [dispatchGuide, setDispatchGuide] = useState('');
  const [packageType, setPackageType] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [dispatchInstructions, setDispatchInstructions] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [slipNumber, setSlipNumber] = useState('');
  const [shipmentInstructions, setShipmentInstructions] = useState ('');

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState(autoT('Error al cargar los datos'));
  const [asnNumber, setAsnNumber] = useState(autoT('Número de ASN'));
  const [showViewAsnDialog, setShowViewAsnDialog] = useState(autoT('Ver ASN'));
  const [isSectionVisible, setIsSectionVisible] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, isUploading: false });
  const [showCreatingDialog, setShowCreatingDialog] = useState(autoT('Creando...'));
  const [showDraftDialog, setShowDraftDialog] = useState(autoT('Guardando borrador...'));
  const [showDraftSuccessDialog, setShowDraftSuccessDialog] = useState(autoT('Borrador guardado con éxito'));

  const [description, setDescription] = useState(autoT('Notificación de la Orden de Compra Nro. {{orderNumber}}', { 
    orderNumber: purchaseOrder?.orderNumber || '' 
  }));

  const [showDateErrorDialog, setShowDateErrorDialog] = useState(autoT('Error en la fecha'));
  const [dateErrorMessage, setDateErrorMessage] = useState(autoT('Fecha inválida'));

  const [showQuantityErrorDialog, setShowQuantityErrorDialog] = useState(autoT('Error en la cantidad'));
  const [quantityErrorMessage, setQuantityErrorMessage] = useState(autoT('Cantidad inválida'));
  
  const [activePart, setActivePart] = useState('');
  const { id } = useParams();
  const [showBultoErrorDialog, setShowBultoErrorDialog] = useState(autoT('Error en el bulto'));
  const [bultoErrorMessage, setBultoErrorMessage] = useState(autoT('Error en el bulto'));
  const [totalPesoBulto, setTotalPesoBulto] = useState(0);
  const [totalVolumenBulto, setTotalVolumenBulto] = useState(0);
  const [asnDocumentId, setAsnDocumentId] = useState('');

  const [pendingQuantities, setPendingQuantities] = useState({});

  const [errorFields, setErrorFields] = useState({
    slipNumber: false,
  });

  const [errorMessages, setErrorMessages] = useState([]);

  // Crear referencias para los campos
  const deliveryDateRef = useRef(null);
  const shipmentDateRef = useRef(null);
  const totalPesoRef = useRef(null);
  const totalVolumenRef = useRef(null);
  const cantidadBultosRef = useRef(null);
  const totalvolumenBultoRef = useRef(null);
  const cargoTypeRef = useRef(null);
  const transportTypeRef = useRef (null);
  const shipmentTypeRef = useRef(null);
  const vehiclePlateRef = useRef(null);
  const slipNumberRef = useRef(null);
  const shipmentInstructionsRef = useRef(null);
  const bultoInputRefs = useRef({}); // Mapa para referencias de inputs de bultos

  const [data, setData] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);
  

  const [dataImport, setDataImport] = useState([]);
  const [shouldUpdateQuantities, setShouldUpdateQuantities] = useState(false);
  const totalPesoTabla = bultos.reduce((acc, bulto) => {
    const peso = parseToNumber(bulto.peso);
    const newAcc = parseToNumber(formatNumberWithComma(acc + peso));
    return newAcc;
  }, 0);
  const totalVolumenTabla = bultos.reduce((acc, bulto) => {
    const volumen = parseToNumber(bulto.volumen);
    return parseToNumber(formatNumberWithComma(acc + volumen));
  }, 0);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const handleStepChange = (event) => {
    const nextStep = event.detail.step - 1;
    
    // Validar fechas antes de permitir avanzar del primer paso
    if (selectedStep === 0 && nextStep === 1) {
      const isDeliveryDateValid = deliveryDate ? validateDate(deliveryDate, 'entrega') : false;
      const isShipmentDateValid = shipmentDate ? validateDate(shipmentDate, 'salida') : false;

      if (!deliveryDate || !shipmentDate) {
        setDateErrorMessage(autoT('Las fechas de entrega y salida son obligatorias'));
        setShowDateErrorDialog(true);
        return;
      }

      if (!isDeliveryDateValid || !isShipmentDateValid) {
        return;
      }
    }

    setSelectedStep(nextStep);
  };

  const handleDispatchQuantityChange = (lineId, newValue) => {
    const numericValue = parseToNumber(newValue);

    const item = purchaseOrder.items.find(item => item.lineId === lineId);
    const originalQuantity = parseToNumber(item?.quantityPending || 0);
    
    // Validar que el valor no sea mayor que la cantidad original
    if (numericValue > originalQuantity) {
      setQuantityErrorMessage(autoT('Valor no puede ser superior al registrado ({{originalQuantity}})', { 
        originalQuantity: originalQuantity 
      }));
      setShowQuantityErrorDialog(true);
      return;
    }

    // Actualizar purchaseOrder.items directamente (no items)
    setPurchaseOrder(prevPO => ({
      ...prevPO,
      items: prevPO.items.map(item => {
        if (item.lineId === lineId) {
          return {
            ...item,
            dispatchQuantity: newValue
          };
        }
        return item;
      })
    }));

    // Actualizar la cantidad pendiente
    const newPendingQuantity = originalQuantity - numericValue;

    setPendingQuantities(prev => {
      const newPendingQuantities = {
        ...prev,
        [lineId]: newPendingQuantity
      };
      return newPendingQuantities;
    });
  };

  const handleFileUpload = (fileType, file) => {
    setAttachments(prev => ({
        ...prev,
        [fileType]: file
    }));
  };

  // Modificar handleLoadPositions para considerar cantidades ya asignadas y eliminar materiales por bulto
  const handleLoadPositions = () => {
    // Usar purchaseOrder.items en lugar de items
    const initialPositions = (purchaseOrder?.items || [])
      .filter(item => parseToNumber(item.dispatchQuantity) > 0)
      .map(item => ({
        lineId: item.lineId,
        materialCode: item.materialCode,
        description: item.description || item.product,
        dispatchQuantity: item.dispatchQuantity,
        packQuantity: item.dispatchQuantity,
        assignedQuantities: []
      }));
    
    setPositions(initialPositions);
    // Clear all materials from existing bultos, but keep the bultos themselves
    setBultos(prevBultos => prevBultos.map(bulto => ({ ...bulto, posiciones: [] })));
    console.log('Cargar Posiciones de materiales activated: Materials cleared from bultos.');
  };

  // Modificar la función que maneja la selección de bulto
  const handleBultoSelection = (lineId, bultoNumber) => {
    if (!bultoNumber) return;

    console.log(`Bulto selected: Line ID: ${lineId}, Bulto Number: ${bultoNumber}`);

    const position = positions.find(p => p.lineId === lineId);
    if (!position || !position.packQuantity) {
      console.log(`Position not found or pack quantity missing for Line ID: ${lineId}`);
      setQuantityErrorMessage(autoT('Debe ingresar una cantidad a embalar'));
      setShowQuantityErrorDialog(true);
      return;
    }

    console.log(`Position found: ${JSON.stringify(position)}`);

    // Actualizar las cantidades asignadas usando parseToNumber para mantener precisión decimal
    setPositions(prevPositions => {
      const updatedPositions = prevPositions.map(pos => {
        if (pos.lineId === lineId) {
          const currentAssigned = pos.assignedQuantities || [];
          const packQty = parseToNumber(pos.packQuantity);
          const totalAssigned = [...currentAssigned, packQty]
            .reduce((sum, qty) => sum + parseToNumber(qty), 0);
          
          // Calcular cantidad disponible con precisión decimal
          const dispatchQty = parseToNumber(pos.dispatchQuantity);
          const remainingQty = dispatchQty - packQty; // Subtract only the current packQuantity

          console.log(`Updated Position: Line ID: ${lineId}, Total Assigned: ${totalAssigned}, Remaining Qty: ${remainingQty}`);
          
          return {
            ...pos,
            assignedQuantities: [...currentAssigned, packQty],
            packQuantity: formatNumberWithComma(remainingQty),
            dispatchQuantity: formatNumberWithComma(remainingQty)
            
          };
        }
        return pos;
      });
      console.log(`Updated Positions: ${JSON.stringify(updatedPositions)}`);
      return updatedPositions;
    });

    // Actualizar el bulto con la posición (lineId)
    setBultos(prevBultos => {
      const updatedBultos = prevBultos.map(bulto => {
        if (bulto.bultoNumber === bultoNumber) {
          const existingPosition = bulto.posiciones?.find(pos => pos.lineId === lineId);
          
          if (existingPosition) {
            // Si la posición ya existe en el bulto, combinar las cantidades
            const updatedBulto = {
              ...bulto,
              posiciones: bulto.posiciones.map(pos => {
                if (pos.lineId === lineId) {
                  const newQuantity = parseToNumber(pos.quantity) + parseToNumber(position.packQuantity);
                  console.log(`Updating existing position in bulto: Line ID: ${lineId}, New Quantity: ${newQuantity}`);
                  return {
                    ...pos,
                    quantity: formatNumberWithComma(newQuantity)
                  };
                }
                return pos;
              })
            };
            console.log(`Updated Bulto with existing position: ${JSON.stringify(updatedBulto)}`);
            return updatedBulto;
          } else {
            // Si la posición no existe, agregarla
            const newBulto = {
              ...bulto,
              posiciones: [
                ...(bulto.posiciones || []),
                {
                  lineId: lineId,
                  materialCode: position.materialCode,
                  description: position.description,
                  quantity: parseToNumber(position.packQuantity)
                }
              ]
            };
            console.log(`Added new position to bulto: ${JSON.stringify(newBulto)}`);
            return newBulto;
          }
        }
        return bulto;
      });
      console.log(`Updated Bultos: ${JSON.stringify(updatedBultos)}`);
      return updatedBultos;
    });
  };

  const handleBultoSelection2 = (item) => {
    const materialCode = item.Material;
    const bultoNumber = String(item.Bulto);
    const packQuantity = Number(item.Cantidad);
    const pos = item.Pos;

    const materialItem = items.find(i => i.materialCode === materialCode);

    if (!materialItem) {
      setQuantityErrorMessage(autoT('Error: Material {{materialCode}} no encontrado', { 
        materialCode: materialCode 
      }));
      setShowQuantityErrorDialog(true);
      return;
    }

    const originalQuantity = parseToNumber(materialItem.quantityOC || 0);
    const pendingQuantity = materialItem.quantityPending;

    if (packQuantity > pendingQuantity) {
      setQuantityErrorMessage(
        autoT('La cantidad de {{materialCode}} es demasiado alta. {{packQuantity}} > {{pendingQuantity}}.', {
          materialCode: materialCode,
          packQuantity: packQuantity,
          pendingQuantity: pendingQuantity
        })
      );
      setShowQuantityErrorDialog(true);
      return;
    }

    // Validar si `bultoNumber` es válido
    if (!bultoNumber) return;
    setShouldUpdateQuantities(true);
    // Actualizar bultos
    setBultos(prevBultos => {
      const newBultos = prevBultos.map(bulto => {
        if (bulto.bultoNumber === bultoNumber) {
          return {
            ...bulto,
            posiciones: [
              ...(bulto.posiciones || []),
              { materialCode, quantity: pos ? packQuantity : 0 },
            ],
          };
        }
        return bulto;
      });
      return [...newBultos];
    });

    // Calcular TotalpackQuantity (acumulación de cantidades asignadas)
    const TotalpackQuantity = bultos.reduce((total, bulto) => {
      if (bulto.posiciones) {
        const cantidadMaterial = bulto.posiciones
          .filter(pos => pos.materialCode === materialCode)
          .reduce((sum, pos) => sum + pos.quantity, 0);
        return total + cantidadMaterial;
      }
      return total;
    }, 0) + packQuantity;

    // Validar que el valor no sea mayor que la cantidad original
    if (TotalpackQuantity > originalQuantity) {
      setQuantityErrorMessage(autoT('Valor no puede ser superior al registrado ({{originalQuantity}})', { 
        originalQuantity: originalQuantity 
      }));
      setShowQuantityErrorDialog(true);
      return;
    }

    // Update dispatch quantity dynamically when sending to a bulto
    setItems(prevItems => prevItems.map(item => {
      if (item.materialCode === materialCode) {
        return {
          ...item,
          dispatchQuantity: item.dispatchQuantity - packQuantity,
        };
      }
      return item;
    }));

    // Update pending quantities to reflect the packed quantities
    setPendingQuantities(prev => ({
      ...prev,
      [materialCode]: originalQuantity - TotalpackQuantity,
    }));

    // Actualizar positions
    setPositions(prevPositions => {
      const existingIndex = prevPositions.findIndex(pos => pos.materialCode === materialCode);

      const newPositions = [...prevPositions];

      if (existingIndex !== -1) {
        // Si el material ya existe, actualizar los valores
        newPositions[existingIndex] = {
          ...newPositions[existingIndex],
          dispatchQuantity: String(TotalpackQuantity),
          packQuantity: (newPositions[existingIndex].packQuantity || 0) + (pos ? 1 : 0),
          assignedQuantities: [...newPositions[existingIndex].assignedQuantities, packQuantity],
        };
      } else {
        // Si es un nuevo material, agregarlo
        newPositions.push({
          materialCode,
          dispatchQuantity: String(TotalpackQuantity),
          packQuantity: pos ? 1 : 0,
          assignedQuantities: [packQuantity],
        });
      }

      return newPositions;
    });
  };
  
  useEffect(() => {
    if (shouldUpdateQuantities && bultos.length > 0) {
      const materialQuantities = {};

      bultos.forEach(bulto => {
        if (bulto.posiciones) {
          bulto.posiciones.forEach(pos => {
            if (!materialQuantities[pos.materialCode]) {
              materialQuantities[pos.materialCode] = 0;
            }
            materialQuantities[pos.materialCode] += pos.quantity;
          });
        }
      });

      Object.entries(materialQuantities).forEach(([materialCode, total]) => {
        // Actualizar items y pendientes después de calcular todo
        setItems(prevItems => prevItems.map(item => {
          if (item.materialCode === materialCode) {
            return { ...item, dispatchQuantity: total };
          }
          return item;
        }));

        setPendingQuantities(prev => ({
          ...prev,
          [materialCode]: parseToNumber(items.find(i => i.materialCode === materialCode)?.quantityPending || 0) - total,
        }));

        setPositions(prevPositions => {
          const existingIndex = prevPositions.findIndex(pos => pos.materialCode === materialCode);
          const newPositions = [...prevPositions];

          if (existingIndex !== -1) {
            newPositions[existingIndex] = {
              ...newPositions[existingIndex],
              dispatchQuantity: String(total),
              assignedQuantities: [...newPositions[existingIndex].assignedQuantities, total],
            };
          } else {
            newPositions.push({
              materialCode,
              dispatchQuantity: String(total),
              assignedQuantities: [total],
            });
          }

          return newPositions;
        });
      });
      setShouldUpdateQuantities(false);
    }
  }, [bultos], shouldUpdateQuantities);

  const handleBultoKeyDown = (event, bultoNumber, field) => {
    if (event.key === 'Tab') {
      event.preventDefault();

      const fieldsOrder = ['peso', 'altura', 'largo', 'ancho'];
      const currentFieldIndex = fieldsOrder.indexOf(field);

      let nextBultoNumber = bultoNumber;
      let nextFieldIndex = currentFieldIndex + 1;

      // Si estamos en el último campo de la fila actual, pasar al primer campo de la siguiente fila
      if (nextFieldIndex >= fieldsOrder.length) {
        nextFieldIndex = 0;
        const currentBultoIndex = bultos.findIndex(b => b.bultoNumber === bultoNumber);
        if (currentBultoIndex < bultos.length - 1) {
          nextBultoNumber = bultos[currentBultoIndex + 1].bultoNumber;
        } else {
          // Si es el último campo del último bulto, no hacer nada para permitir el tab out del modal
          return; 
        }
      }

      const nextField = fieldsOrder[nextFieldIndex];
      const nextInputRef = bultoInputRefs.current[`${nextBultoNumber}-${nextField}`];

      if (nextInputRef) {
        nextInputRef.focus();
        // Seleccionar todo el texto para facilitar la edición
        setTimeout(() => {
            // Acceder al elemento de entrada nativo si está dentro de shadow DOM
            const inputElement = nextInputRef.shadowRoot?.querySelector('input') || nextInputRef;
            if (inputElement && inputElement.select) {
                inputElement.select();
            }
        }, 0);
      }
    }
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const openExpandedModal = () => setIsExpandedOpen(true);
  const closeExpandedModal = () => setIsExpandedOpen(false);

  // Columnas para la tabla de posiciones
  const positionColumns = [
    { 
      Header: autoT('Posición'), 
      accessor: 'lineId' 
    },
    { 
      Header: autoT('Cantidad a Despachar'), 
      accessor: 'dispatchQuantity',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value) || 0}</Text>
      ),
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    },
    { 
      Header: autoT('Cantidad a Embalar'), 
      accessor: 'packQuantity',
      hAlign: "End",
      Cell: ({ row, value }) => {
        const dispatchQty = parseToNumber(row.original.dispatchQuantity) || 0;
        const assignedQty = (row.original.assignedQuantities || [])
          .reduce((sum, qty) => sum + parseToNumber(qty), 0);
        const availableQty = formatNumberWithComma((dispatchQty - assignedQty))
        
        return (
          <Input
            type="text"
            value={formatNumberWithComma(value) || availableQty}
            onChange={(e) => {
              const newValue = e.target.value.replace(/[^0-9,]/g, '');
              if (parseToNumber(newValue) <= dispatchQty) {
                handlePackQuantityChange(row.original.lineId, newValue);
              } else {
                setQuantityErrorMessage(
                  autoT('La cantidad a embalar no puede superar {{dispatchQty}}', {
                    dispatchQty: formatNumberWithComma(dispatchQty)
                  })
                );
                setShowQuantityErrorDialog(true);
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              const formattedValue = parseToNumber(value)
              handlePackQuantityChange(row.original.lineId, formattedValue);
            }}
            style={{ width: '100px', textAlign: 'right' }}
            placeholder={availableQty.toString()}
          />
        );
      }
    },
    { 
      Header: autoT('Acciones'),
      accessor: 'actions',
      Cell: ({ row }) => (
        <Select
          onChange={(e) => handleBultoSelection(row.original.lineId, e.target.value)}
          style={{ width: '150px' }}
        >
          <Option value="">{autoT('Seleccionar Bulto')}</Option>
          {bultos.map((bulto) => (
            <Option key={bulto.bultoNumber} value={bulto.bultoNumber}>
              {autoT('Bulto {{bultoNumber}}', { bultoNumber: bulto.bultoNumber })}
            </Option>
          ))}
        </Select>
      )
    }
  ];

  useEffect(() => {
    console.log("Estado actualizado de positions:", positions);
  }, [positions]); // Se ejecuta cada vez que positions cambie
  // Agregar función para manejar el cambio de cantidad a embalar
  const handlePackQuantityChange = (lineId, quantity) => {
    setPositions(prevPositions => 
      prevPositions.map(pos => {
        if (pos.lineId === lineId) {
          return {
            ...pos,
            packQuantity: quantity
          };
        }
        return pos;
      })
    );
  };
  // Función para calcular el volumen
  const calcularVolumen = (altura, largo, ancho) => {
    try {
        const alturaNum = typeof altura === 'number' ? altura : parseToNumber(altura.toString());
        const largoNum = typeof largo === 'number' ? largo : parseToNumber(largo.toString());
        const anchoNum = typeof ancho === 'number' ? ancho : parseToNumber(ancho.toString());
        
        // Validar entradas
        if (isNaN(alturaNum) || isNaN(largoNum) || isNaN(anchoNum)) {
            return formatNumberWithComma(0);
        }
        
        // Calcular volumen (altura * largo * ancho)
        const vol = alturaNum * largoNum * anchoNum;
        
        return formatNumberWithComma(vol);
    } catch (error) {
        return formatNumberWithComma(0);
    }
  };
  
  // Función para actualizar un bulto
  const handleBultoUpdate = (bultoNumber, field, value) => {
    if (typeof value === 'string' && (value.includes(',') || value.includes('9'))) {
      console.log(`Valor de bulto modificado para bulto ${bultoNumber}, campo ${field}: ${value}`);
    }
    setBultos(prev => prev.map(bulto => {
        if (bulto.bultoNumber === bultoNumber) {
            const numericValue = parseToNumber(value);
            const newBulto = { ...bulto, [field]: numericValue };
            
            // Recalcular volumen si se modificó alguna dimensión
            if (['altura', 'largo', 'ancho'].includes(field)) {
                newBulto.volumen = calcularVolumen(
                    field === 'altura' ? numericValue : parseToNumber(bulto.altura),
                    field === 'largo' ? numericValue : parseToNumber(bulto.largo),
                    field === 'ancho' ? numericValue : parseToNumber(bulto.ancho)
                );
            }
            return newBulto;
        }
        return bulto;
    }));
  };

  // Modificar los columnas de la tabla de bultos
  const bultoColumns = [
    { 
      Header: autoT('Bulto'), 
      accessor: 'bultoNumber',
      Cell: ({ value }) => autoT('Bulto {{bultoNumber}}', { bultoNumber: value })
    },
    { 
      Header: autoT('Material'), 
      accessor: 'materialCode'
    },
    { 
      Header: autoT('Cantidad'), 
      accessor: 'quantity',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value) || 0}</Text>
      ),
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    },
    { 
      Header: autoT('Volumen - m³'),
      accessor: 'volumen',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(parseToNumber(value))}</Text>
      ),
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    }
  ];

  // Definir todas las columnas al inicio del componente
  const itemColumns = [
    { Header: autoT('Item PO'), accessor: 'lineId' },
    { Header: autoT('Código de material'), accessor: 'materialCode' },
    { Header: autoT('Producto'), accessor: 'description' },
    { 
      Header: autoT('Cant. OC'), 
      accessor: 'quantity', 
      Cell: ({ value }) => formattedNumberCell(value).content,
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    },
    { 
      Header: autoT('Cant. recibida de OC'), 
      accessor: 'quantityReceived', 
      Cell: ({ value }) => formattedNumberCell(value).content,
      style: rightAlignedNumberStyle(),
      hAlign: "End" 
    },
    { 
      Header: autoT('Notif. Emitidas'), 
      accessor: 'quantitySent', 
      Cell: ({ value }) => formattedNumberCell(value).content,
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    },
    { 
      Header: autoT('Notif. no Enviada'), 
      accessor: '', 
      Cell: ({ value }) => value ? formattedNumberCell(value).content : '',
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    },
    {
      Header: autoT('Cant. Pendiente'),
      accessor: 'quantityPending',
      Cell: ({ row }) => (
        <Text style={rightAlignedNumberStyle()}>
          {formatNumberWithComma(pendingQuantities[row.original.lineId] || row.original.quantityPending || 0)}
        </Text>
      ),
      style: rightAlignedNumberStyle(),
      hAlign: "End"
    },
    {
      Header: autoT('Cant. a Despachar'),
      accessor: 'dispatchQuantity',
      hAlign: "End",
      Cell: ({ row }) => {
        const lineId = row.original.lineId;
        // Buscar en row.original directamente (purchaseOrder.items)
        const dispatchQuantity = row.original.dispatchQuantity || '';
    
        const handleInputChange = (e) => {
          let value = e.target.value;
    
          // Permitir solo números con coma y hasta 3 decimales
          const regex = /^\d*(?:,\d{0,3})?$/;
    
          if (regex.test(value)) {
            handleDispatchQuantityChange(lineId, value);
          }
        };  
    
        return (
          <Input
            type="text"
            inputMode="decimal"
            value={formatNumberWithComma(dispatchQuantity)}
            onChange={handleInputChange}
            onBlur={(e) => {
              let value = e.target.value;
              // Formatear a 3 decimales al perder el foco
              const formattedValue = parseToNumber(value);
              handleDispatchQuantityChange(lineId, formattedValue);
            }}
            style={{ width: '100px', textAlign: 'right' }}
            placeholder="0"
          />
        );
      }
    }
  ];

  const attachmentColumns = [
    { Header: autoT('Nombre'), accessor: 'name' },
    { Header: autoT('Tipo'), accessor: 'type' },
    { Header: autoT('Tamaño'), accessor: 'size' },
    { Header: autoT('Fecha'), accessor: 'uploadDate' },
    { Header: autoT('Estado'), accessor: 'status' }
  ];


  // Función para validar si la primera parte está completa
  const isFirstStepComplete = () => {
    return (
      description.trim() !== '' &&
      cantidadBultos > 0 &&
      parseToNumber(totalVolumenBulto) > 0 &&
      parseToNumber(totalPesoBulto) > 0 &&
      shipmentInstructions.trim() !== '' &&
      deliveryDate &&
      shipmentDate &&
      cargoType &&
      transportType &&
      shipmentType &&
      vehiclePlate.trim() !== '' &&
      slipNumber.trim() !== ''
    );
  };

  // Función para validar fechas
  const validateDate = (selectedDate, fieldName) => {
    if (!selectedDate) {
      setDateErrorMessage(autoT('La fecha de {{fieldName}} es obligatoria', { fieldName }));
      setShowDateErrorDialog(true);
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dateToValidate = new Date(selectedDate);
    dateToValidate.setHours(0, 0, 0, 0);

    if (dateToValidate < today) {
      setDateErrorMessage(autoT('La fecha de {{fieldName}} debe ser igual o superior al día de hoy', { fieldName }));
      setShowDateErrorDialog(true);
      return false;
    }
    return true;
  };

  // Función para formatear fecha a yyyy-MM-dd
  const getDateOnly = (date) => {
    if (!date) return null;
    
    try {
      // Si es string en formato yyyy-MM-dd, retornarlo directamente
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // Si es string, convertir a objeto Date
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Validar que sea una fecha válida
      if (!(dateObj instanceof Date) || isNaN(dateObj)) {
        return null;
      }

      // Formatear a yyyy-MM-dd
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      return null;
    }
  };

  // Función para formatear fecha completa
  const formatDate = (date) => {
    if (!date) return null;
    
    // Si es string, convertir a objeto Date
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Validar que sea una fecha válida
    if (!(dateObj instanceof Date) || isNaN(dateObj)) {
      return null;
    }

    // Formatear a yyyy-MM-dd
    return getDateOnly(dateObj);
  };

  // Función para convertir fecha de dd/MM/yyyy a yyyy-MM-dd
  const convertDateFormat = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Si ya está en formato yyyy-MM-dd, retornarlo
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Convertir de dd/MM/yyyy a yyyy-MM-dd
      const [day, month, year] = dateString.split('/');
      if (!day || !month || !year) return null;
      
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    } catch (error) {
      return null;
    }
  };

  // Modificar la función formatDateWithTime para agregar logs
  const formatDateWithTime = (dateString) => {
    if (!dateString) {
      return null;
    }
    
    try {
      // Si ya está en formato completo, retornarlo
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Si es formato yyyy-MM-dd, agregar tiempo
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const result = `${dateString}T00:00:00`;
        return result;
      }

      // Si es dd/MM/yyyy, convertir y agregar tiempo
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('/');
        const result = `${year}-${month}-${day}T00:00:00`;
        return result;
      }

      // Si es un objeto Date
      if (dateString instanceof Date) {
        const result = dateString.toISOString().split('.')[0];
        return result;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Función para validar totales de bultos
  const validateBultoTotals = () => {
    // Calcular el peso total manteniendo la precisión decimal
    const pesoTotal = bultos.reduce((sum, bulto) => {
      const peso = parseToNumber(bulto.peso || 0);
      return parseToNumber(formatNumberWithComma(sum + peso));
    }, 0);

    // Calcular el volumen total manteniendo la precisión decimal
    const volumenTotal = bultos.reduce((sum, bulto) => {
      const volumen = parseToNumber(bulto.volumen || 0);
      return parseToNumber(formatNumberWithComma(sum + volumen));
    }, 0);

    // Formatear los valores para comparación
    const pesoTotalFormatted = formatNumberWithComma(pesoTotal);
    const volumenTotalFormatted = formatNumberWithComma(volumenTotal);
    const totalPesoBultoFormatted = formatNumberWithComma(parseToNumber(totalPesoBulto));
    const totalVolumenBultoFormatted = formatNumberWithComma(parseToNumber(totalVolumenBulto));

    console.log('Validación de totales:', {
      pesoCalculado: pesoTotalFormatted,
      pesoRegistrado: totalPesoBultoFormatted,
      volumenCalculado: volumenTotalFormatted,
      volumenRegistrado: totalVolumenBultoFormatted
    });

    // Comparar los valores formateados como strings para evitar problemas de precisión
    if (pesoTotalFormatted !== totalPesoBultoFormatted) {
      setBultoErrorMessage(
        `El peso total de los bultos (${pesoTotalFormatted} kg) no coincide con el peso total registrado (${totalPesoBultoFormatted} kg)`
      );
      setShowBultoErrorDialog(true);
      return false;
    }

    if (volumenTotalFormatted !== totalVolumenBultoFormatted) {
      setBultoErrorMessage(
        `El volumen total de los bultos (${volumenTotalFormatted} m³) no coincide con el volumen total registrado (${totalVolumenBultoFormatted} m³)`
      );
      setShowBultoErrorDialog(true);
      return false;
    }

    return true;
  };

  // Función para bloquear/desbloquear la orden
  const setOrderLocked = async (locked) => {
    if (!purchaseOrder?.id) return;
    try {
      const userDataString = localStorage.getItem('userData');
      const userData = JSON.parse(userDataString);
      const modifiedBy = userData.id
      const body = { modifiedBy, locked };
      console.log('Enviando locked' + purchaseOrder.id, body);
      await axiosInstance.put(`purchase-orders/${purchaseOrder.id}`, body);
    } catch (error) {
      console.error('Error actualizando locked:', error);
    }
  };

  // Bloquear al montar, desbloquear al desmontar
  useEffect(() => {
    setOrderLocked(true);
    return () => {
      setOrderLocked(false);
    };
    // Solo cuando cambia la orden
  }, [purchaseOrder?.id]);

  // Inactividad: Expulsar tras 15 minutos y desbloquear la orden
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(10);
  const inactivityWarningTimer = useRef(null);
  const inactivityMainTimer = useRef(null);

  useEffect(() => {
    if (!purchaseOrder?.id) return;
    const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutos
    const WARNING_COUNTDOWN = 10; // segundos

    const handleFinalInactivity = async () => {
      await setOrderLocked(false);
      setShowInactivityWarning(false);
      navigate('/home', { state: { inactive: true } });
    };

    const startWarningCountdown = () => {
      setInactivityCountdown(WARNING_COUNTDOWN);
      setShowInactivityWarning(true);
      inactivityWarningTimer.current = setInterval(() => {
        setInactivityCountdown(prev => {
          if (prev <= 1) {
            clearInterval(inactivityWarningTimer.current);
            handleFinalInactivity();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const resetAllTimers = () => {
      clearTimeout(inactivityMainTimer.current);
      clearInterval(inactivityWarningTimer.current);
      setShowInactivityWarning(false);
      setInactivityCountdown(WARNING_COUNTDOWN);
      inactivityMainTimer.current = setTimeout(startWarningCountdown, INACTIVITY_LIMIT);
    };

    // Eventos de actividad
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetAllTimers));
    resetAllTimers();

    return () => {
      clearTimeout(inactivityMainTimer.current);
      clearInterval(inactivityWarningTimer.current);
      events.forEach(event => window.removeEventListener(event, resetAllTimers));
    };
  }, [purchaseOrder?.id, navigate]);

  // Función para guardar borrador
  const handleSaveDraft = async () => {
    setIsSaving(true);
    setShowCreatingDialog(true);
    try {
      const userDataString = localStorage.getItem('userData');
      const userData = JSON.parse(userDataString);
      const CreatedBy = userData.id;

      const asnDocumentId = crypto.randomUUID();
      
      // Convertir las fechas
      const convertedDeliveryDate = convertDateFormat(deliveryDate);
      const convertedShipmentDate = convertDateFormat(shipmentDate);
      
      const formattedDeliveryDate = formatDateWithTime(convertedDeliveryDate);
      const formattedShipmentDate = formatDateWithTime(convertedShipmentDate);
      const formattedAsnDate = formatDateWithTime(new Date());

      // Validar que la conversión fue exitosa
      if (!formattedDeliveryDate || !formattedShipmentDate || !formattedAsnDate) {
        throw new Error('Error al formatear las fechas. Por favor, verifique el formato de las fechas ingresadas.');
      }
      // Usar purchaseOrder.items en lugar de items
      const selectedItems = (purchaseOrder?.items || [])
      .filter(item => parseToNumber(item.dispatchQuantity) > 0)
      .map((item) => {
        return {
          id: crypto.randomUUID(),
          asnDocumentId,
          lineId: item.lineId,
          materialCode: item.materialCode,
          description: item.product || item.description || "",
          quantity: parseToNumber(item.dispatchQuantity),
          unit: item.unit,
          pricePerUnit: item.pricePerUnit || 0,
          totalPrice: (item.pricePerUnit || 0) * parseToNumber(item.dispatchQuantity),
          receivedQuantity: 0,
          asnSentQuantity: 0,
          poNumber: purchaseOrder?.orderNumber,
          positionPo: item.id
        };
      });


      // Construir el payload de paquetes igual que en el guardado normal
      const packagesPayload = bultos.map(bulto => ({
        asnDocumentId,
        packageNumber: bulto.bultoNumber,
        weight: parseToNumber(bulto.peso),
        volume: parseToNumber(bulto.volumen),
        dimensions: `${bulto.altura}x${bulto.largo}x${bulto.ancho} m`,
        packingLists: bulto.posiciones ? bulto.posiciones.map(pos => ({
          asnDocumentId,
          packageNumber: bulto.bultoNumber,
          packageCodebars: bulto.bultoNumber,
          rfidEpc: "",
          weight: parseToNumber(bulto.peso),
          volume: parseToNumber(bulto.volumen),
          length: parseToNumber(bulto.largo),
          width: parseToNumber(bulto.ancho),
          height: parseToNumber(bulto.altura),
          itemDescription: pos.materialCode,
          quantity: parseToNumber(pos.quantity)
        })) : []
      }));

      const payload = {
        asnNumber: `ASN-DRAFT-${Date.now()}`,
        purchaseOrderId: purchaseOrder?.id,
        id: asnDocumentId,
        asnDate: formattedAsnDate,
        deliveryDate: formattedDeliveryDate,
        deliveryTime: formattedDeliveryDate,
        totalWeight: bultos.reduce((sum, b) => sum + parseToNumber(b.peso), 0),
        totalVolume: bultos.reduce((sum, b) => sum + parseToNumber(b.volumen), 0),
        shipmentType: shipmentType || "",
        shipmentDate: formattedShipmentDate,
        shipmentTime: formattedShipmentDate,
        transportMedium: transportType || "Truck",
        transportId: 1,
        chargeType: cargoType || "Normal",
        slipNumber: parseInt(slipNumber || "0"),
        packageQty: parseInt(cantidadBultos || "0"),
        packageUnit: 1,
        packageType: packageType || "Carton",
        vehiclePlate: vehiclePlate || "",
        shipmentInstructions: shipmentInstructions || "",
        asnStatus: "Draft", // Estado de borrador
        eeNumber: "EE-" + Math.floor(Math.random() * 1000),
        items: selectedItems,
        packages: packagesPayload, // Incluir paquetes igual que en guardado normal
        createdBy: CreatedBy,
        description: description
      };

      console.log('Payload de borrador a enviar:', JSON.stringify(payload, null, 2));

      // Enviar solo al endpoint específico de draft
      const response = await axiosInstance.post('asn-documents/draft', payload);
      setAsnNumber(response.data.data.asnNumber);
      setAsnDocumentId(response.data.data.id);

      setShowDraftSuccessDialog(true);
    } catch (error) {
      console.error('Error guardando borrador:', error);
      setErrorMessage(`Error al guardar el borrador: ${error?.message || error}`);
      setShowErrorDialog(true);
    } finally {
      setIsSaving(false);
      setShowCreatingDialog(false);
      setOrderLocked(false);
    }
  };

  // Modificar handleSubmit para incluir la validación y el enfoque
  const handleSubmit = async () => {
    setIsSaving(true); // Activar spinner y deshabilitar botón
    setShowCreatingDialog(true); // Mostrar modal de "Creando Notificación"
    try {
      // Validar que slipNumber sea numérico
      if (errorFields.slipNumber) {
        slipNumberRef.current.focus();
        throw new Error('El valor de Guia de Despacho debe ser numérico.');
      }

 

      // Validar totales de bultos antes de continuar
      if (!validateBultoTotals()) {
        totalPesoRef.current.focus(); // Enfocar el campo de peso
        throw new Error('Los bultos deben coincidir')
      }

      // Validar que los datos básicos no sean 0
      if (cantidadBultos <= 0) {
        cantidadBultosRef.current.focus();
        throw new Error('La cantidad de bultos no puede ser 0 o menor.');
      } else if (parseToNumber(totalVolumenBulto) <= 0) {
        totalVolumenRef.current.focus();
        throw new Error('El volumen de bulto no puede ser 0 o menor.');
      } else if (parseToNumber(totalPesoBulto) <= 0) {
        totalPesoRef.current.focus();
        throw new Error('El peso de bulto no puede ser 0 o menor.');
      }
      
      // Validar que las fechas existan
      if (!deliveryDate || !shipmentDate) {
        if (!deliveryDate) {
          deliveryDateRef.current.focus(); // Enfocar el campo de fecha de entrega
        } else {
          shipmentDateRef.current.focus(); // Enfocar el campo de fecha de salida
        }
        throw new Error('Las fechas de entrega y envío son obligatorias');
      }

      console.log('Fechas originales:', {
        deliveryDate,
        shipmentDate
      });

      if (!cargoType || !transportType || !shipmentType){
        console.log('datos de cargo transporte y envio: ', cargoType, transportType, shipmentType)
        if(!cargoType){
          cargoTypeRef.current.focus();
          throw new Error('El tipo de carga, bulto y placa patente son datos obligatorios 1')
        }else if (!transportType) {
          transportTypeRef.current.focus();
          throw new Error('El tipo de carga, bulto y placa patente son datos obligatorios. 2');
        }
      }

      if(!vehiclePlate || !slipNumber || shipmentInstructions){
        console.log(vehiclePlate, slipNumber, shipmentInstructions)
        if(!vehiclePlate){
          vehiclePlateRef.current.focus();
          throw new Error ('Patente, guia despacho e instrucción de despacho son datos obligatorios, recordar que Guia de despacho es númerico 1')
        }else if(!slipNumber){
          slipNumberRef.current.focus();
          throw new Error ('Patente, guia despacho e instrucción de despacho son datos obligatorios, recordar que Guia de despacho es númerico 2')
        }else if (!shipmentInstructions){
          shipmentInstructionsRef.current.focus();
          throw new Error ('Patente, guia despacho e instrucción de despacho son datos obligatorios, recordar que Guia de despacho es númerico 3')
        }
      }
        // Validar que los datos de materiales por bulto no estén vacíos
        if (bultos.length === 0 || bultos.every(bulto => bulto.posiciones.length === 0)) {
        throw new Error('Los materiales por bulto no pueden estar vacíos. Por favor, asegúrate de agregar materiales a los bultos.');
      }
      const transportValue = parseToNumber(transportType.replace(/,/g, '.').match(/[\d,.]+/)?.[0] || 1); // Reemplazar comas por puntos y extraer el primer número encontrado o considerar 1      console.log("HOLA",transportValue)
      const isHeavyTransport = transportType && 
      (transportType.includes('15 Toneladas') || 
       transportType.includes('17 Toneladas') || 
       transportType.includes('28 Toneladas'));
      
      if (!attachments.dispatchGuide) {
          throw new Error('Debes adjuntar la Guia de Despacho');
      }
      
      if (isHeavyTransport && !attachments.indivisibleCertificate) {
         // throw new Error('Debes adjuntar el Certificado Indivisible para transportes pesados');
      }
      
      if (cargoType === "Producto Quimico" && !attachments.safetySheet) {
         // throw new Error('Debes adjuntar la Hoja de Seguridad para productos químicos');
      }

      const userDataString = localStorage.getItem('userData');

      // Paso 2: Parsear la cadena JSON a un objeto JavaScript
      const userData = JSON.parse(userDataString);

      // Paso 3: Acceder a la propiedad 'id' del objeto
      const CreatedBy = userData.id;

      const asnDocumentId = crypto.randomUUID();
      // Convertir las fechas y mostrar resultados intermedios
      const convertedDeliveryDate = convertDateFormat(deliveryDate);
      const convertedShipmentDate = convertDateFormat(shipmentDate);
      
      console.log('Fechas después de convertDateFormat:', {
        convertedDeliveryDate,
        convertedShipmentDate
      });

      // Formatear con hora
      const formattedDeliveryDate = formatDateWithTime(convertedDeliveryDate);
      const formattedShipmentDate = formatDateWithTime(convertedShipmentDate);
      const formattedAsnDate = formatDateWithTime(new Date());

      console.log('Fechas finales formateadas:', {
        formattedDeliveryDate,
        formattedShipmentDate,
        formattedAsnDate
      });

      // Validar que la conversión fue exitosa
      if (!formattedDeliveryDate || !formattedShipmentDate || !formattedAsnDate) {
        throw new Error('Error al formatear las fechas. Por favor, verifique el formato de las fechas ingresadas.');
      }
      if (!purchaseOrder?.items || purchaseOrder.items.length === 0) {
        throw new Error('Items vacios');
      }
      // Usar purchaseOrder.items en lugar de items
      const selectedItems = (purchaseOrder?.items || [])
        .filter(item => parseToNumber(item.dispatchQuantity) > 0)
        .map((item) => {
          return {
            id: crypto.randomUUID(),
            asnDocumentId,
            lineId: item.lineId,
            materialCode: item.materialCode,
            description: item.product || item.description || "",
            quantity: parseToNumber(item.dispatchQuantity),
            unit: item.unit,
            pricePerUnit: item.pricePerUnit || 0,
            totalPrice: (item.pricePerUnit || 0) * parseToNumber(item.dispatchQuantity),
            receivedQuantity: 0,
            asnSentQuantity: 0,
            poNumber: purchaseOrder?.orderNumber,
            positionPo: item.id
          };
        });

      const packagesPayload = bultos.map(bulto => ({
        asnDocumentId,
        packageNumber: bulto.bultoNumber,
        weight: parseToNumber(bulto.peso),
        volume: parseToNumber(bulto.volumen),
        dimensions: `${bulto.altura}x${bulto.largo}x${bulto.ancho} m`,
        packingLists: bulto.posiciones ? bulto.posiciones.map(pos => ({
          asnDocumentId,
          packageNumber: bulto.bultoNumber,
          packageCodebars: bulto.bultoNumber,
          rfidEpc: "",
          weight: parseToNumber(bulto.peso),
          volume: parseToNumber(bulto.volumen),
          length: parseToNumber(bulto.largo),
          width: parseToNumber(bulto.ancho),
          height: parseToNumber(bulto.altura),
          lineId: pos.lineId,
          itemDescription: pos.materialCode,
          quantity: parseToNumber(pos.quantity)
        })) : []
      }));

      const payload = {
        asnNumber: `ASN-${Date.now()}`,
        purchaseOrderId: purchaseOrder?.id,
        id: asnDocumentId,
        asnDate: formattedAsnDate,
        deliveryDate: formattedDeliveryDate,
        deliveryTime: formattedDeliveryDate,
        totalWeight: bultos.reduce((sum, b) => sum + parseToNumber(b.peso), 0),
        totalVolume: bultos.reduce((sum, b) => sum + parseToNumber(b.volumen), 0),
        shipmentType: shipmentType,
        shipmentDate: formattedShipmentDate,
        shipmentTime: formattedShipmentDate,
        transportMedium: transportType || "Truck",
        transportId: 1,
        chargeType: cargoType || "Normal",
        slipNumber: parseInt(slipNumber || "0"),
        packageQty: parseInt(cantidadBultos || "0"),
        packageUnit: 1,
        packageType: packageType || "Carton",
        vehiclePlate: vehiclePlate || "",
        shipmentInstructions: shipmentInstructions || "",
        asnStatus: "Pending",
        eeNumber: "EE-" + Math.floor(Math.random() * 1000),
        items: selectedItems,
        packages: packagesPayload,
        createdBy: CreatedBy,
        rampAssignmentStatus: "PENDING"
      };

      // Imprimir el payload en formato JSON
      console.log('Payload a enviar:', JSON.stringify(payload, null, 2));

      const response = await axiosInstance.post('asn-documents', payload);
      setAsnNumber(response.data.data.asnNumber);
      
      // Usar el ID real devuelto por el backend
      const realAsnDocumentId = response.data.data.id;
      setAsnDocumentId(realAsnDocumentId);

      // Enviar los archivos adjuntos uno por uno a la API de ASN Attachments
      const attachmentKeys = Object.keys(attachments);
      if (attachmentKeys.length > 0) {
        setUploadProgress({ current: 0, total: attachmentKeys.length, isUploading: true });
        
        for (let i = 0; i < attachmentKeys.length; i++) {
          const sectionKey = attachmentKeys[i];
          const attachment = attachments[sectionKey];
          
          // Determinar si el adjunto es obligatorio
          const isMandatory = attachmentSections[sectionKey]?.isRequired || 
                             (sectionKey === 'dispatchGuide' || sectionKey === 'deliveryNotice');
          
          const attachmentPayload = {
            asnDocumentId: realAsnDocumentId, // Usar el ID real del backend
            attachmentName: attachment.name || attachment.file?.name || 'archivo_sin_nombre',
            attachmentType: "asn-attachments",
            filePath: attachment.filePath,
            mandatory: isMandatory,
            createdDate: attachment.uploadDate || new Date().toISOString()
          };
          
          try {
            await axiosInstance.post('attachments', attachmentPayload);
            setUploadProgress({ current: i + 1, total: attachmentKeys.length, isUploading: true });
          } catch (error) {
            console.error(`Error enviando adjunto ${sectionKey}:`, error);
            throw new Error(`Error al cargar archivo ${attachment.name || sectionKey}`);
          }
        }
        
        setUploadProgress({ current: 0, total: 0, isUploading: false });
      }


      // Si hay un agendamiento seleccionado, actualizarlo con el número de ASN
      if (selectedSchedule) {
        await axiosInstance.put(`purchase-order-schedules/${selectedSchedule.id}`, {
          asnNumber: response.data.data.asnNumber
        });
      }

      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error detallado:', error);
      setErrorMessage(`Error al guardar la notificación: ${error?.message || error}`);
      console.log(error)
      setShowErrorDialog(true);
    } finally {
      setIsSaving(false); // Desactivar spinner y reactivar botón
      setShowCreatingDialog(false); // Ocultar modal de "Creando Notificación"
      // Desbloquear la orden
      setOrderLocked(false);
    }
  };

  const handleDataImport = (importedData) => {
    console.log("Datos importados:", importedData);

    // Llamar a handleBultoSelection con materialCode y bultoNumber
    importedData.forEach(item => {
      handleBultoSelection2(item);
    });
  };
  // Función para preparar los datos de la tabla de bultos
  const getBultoTableData = (bultos) => {
    return bultos.flatMap(bulto => {
      // Si no hay posiciones, retornar una fila con el bulto vacío
      if (!bulto.posiciones || bulto.posiciones.length === 0) {
        return [{
          bultoNumber: bulto.bultoNumber,
          lineId: '-',
          materialCode: '-',
          description: '-',
          quantity: '0,000',
          peso: formatNumberWithComma(bulto.peso),
          altura: formatNumberWithComma(bulto.altura),
          largo: formatNumberWithComma(bulto.largo),
          ancho: formatNumberWithComma(bulto.ancho),
          volumen: formatNumberWithComma(bulto.volumen)
        }];
      }
      
      // Si hay posiciones, crear una fila por cada posición
      return bulto.posiciones.map(posicion => ({
        bultoNumber: bulto.bultoNumber,
        lineId: posicion.lineId || '-',
        materialCode: posicion.materialCode,
        description: posicion.description || '-',
        quantity: formatNumberWithComma(posicion.quantity),
        peso: formatNumberWithComma(bulto.peso),
        altura: formatNumberWithComma(bulto.altura),
        largo: formatNumberWithComma(bulto.largo),
        ancho: formatNumberWithComma(bulto.ancho),
        volumen: formatNumberWithComma(bulto.volumen)
      }));
    });
  };

  // Modificar los manejadores de cambio de fecha
  const handleDeliveryDateChange = (event) => {
    const selectedDate = event.target.value;
    console.log('Delivery Date Selected:', selectedDate);
    
    if (validateDate(selectedDate, 'entrega')) {
      setDeliveryDate(selectedDate);
    } else {
      setDeliveryDate('');
    }
  };

  const handleShipmentDateChange = (event) => {
    const selectedDate = event.target.value;
    console.log('Shipment Date Selected:', selectedDate);
    
    if (validateDate(selectedDate, 'salida')) {
      setShipmentDate(selectedDate);
    } else {
      setShipmentDate('');
    }
  };

  // Agregar manejadores para los totales
  const handleTotalPesoChange = (event) => {
    const value = event.target.value.replace(/[^0-9,]/g, '');
    // No formatear inmediatamente, solo almacenar el valor
    setTotalPesoBulto(value);
  };

  const handleTotalVolumenChange = (event) => {
    const value = event.target.value.replace(/[^0-9,]/g, '');
    // No formatear inmediatamente, solo almacenar el valor
    setTotalVolumenBulto(value);
  };

  // Agregar manejadores de onBlur para estos campos
  const handleTotalPesoBlur = (event) => {
    const value = event.target.value;
    const formattedValue = formatNumberWithComma(value);
    setTotalPesoBulto(formattedValue);
  };

  const handleTotalVolumenBlur = (event) => {
    const value = event.target.value;
    const formattedValue = formatNumberWithComma(value);
    setTotalVolumenBulto(formattedValue);
  };

  // Modificar la función para reiniciar los bultos
  const handleClearBultos = () => {
    // Reiniciar los bultos con la misma cantidad pero valores en cero
    const bultosIniciales = Array.from({ length: parseInt(cantidadBultos) }, (_, index) => ({
      bultoNumber: (index + 1).toString(),
      posiciones: [],
      peso: '0',
      altura: '0',
      largo: '0',
      ancho: '0',
      volumen: '0.000'
    }));
    
    setBultos(bultosIniciales);
    
    // Restablecer las posiciones con los items que tienen cantidad
    setPositions([...items.filter(item => parseToNumber(item.dispatchQuantity) > 0).map(item => ({
      lineId: item.lineId,
      materialCode: item.materialCode,
      description: item.description || item.product,
      dispatchQuantity: item.dispatchQuantity
    }))]);
    
    // No reiniciar los totales de peso y volumen
    // setTotalPesoBulto(0);
    // setTotalVolumenBulto(0);
  };

  // Inicializar pendingQuantities cuando se carga purchaseOrder
  useEffect(() => {
    if (purchaseOrder?.items && Object.keys(pendingQuantities).length === 0) {
      const initialPendingQuantities = {};
      purchaseOrder.items.forEach(item => {
        initialPendingQuantities[item.lineId] = parseToNumber(item.quantityPending || 0);
      });
      setPendingQuantities(initialPendingQuantities);
    }
  }, [purchaseOrder]);


  // Modificar también la función que calcula el volumen total
  const calculateTotalVolume = () => {
    const total = bultos.reduce((sum, bulto) => {
      const volume = parseToNumber(bulto.volumen);
      console.log(`Sumando volumen de bulto ${bulto.bultoNumber}: ${formatNumberWithComma(volume)}`);
      return parseToNumber(formatNumberWithComma(sum + volume)); // Format and parse to maintain precision
    }, 0);
    
    console.log(`Volumen total calculado: ${formatNumberWithComma(total)}`);
    return total;
  };

  // Modificar la función que maneja el cambio de volumen
  const handleVolumenChange = (index, value) => {
    const sanitizedValue = value.replace(/[^0-9,]/g, '');
    
    setBultos(prevBultos => {
        const newBultos = [...prevBultos];
        // Convertir a número y limitar a 3 decimales
        newBultos[index].volumen = parseToNumber(sanitizedValue); // Usar coma
        return newBultos;
    });
};
  // Función para calcular y mostrar el total de volumen de bultos
  const calculateAndDisplayTotalVolume = () => {
    const total = calculateTotalVolume();
    console.log(`Total de volumen para mostrar: ${formatNumberWithComma(total)}`);
    return formatNumberWithComma(total);
};
  // Modificar también la función que actualiza los totales
  const updateTotals = () => {
    const totalVolumen = bultos.reduce((sum, bulto) => {
      return sum + parseToNumber(bulto.volumen || 0);
    }, 0);
    
    setTotalVolumenBulto(formatNumberWithComma(totalVolumen));
  };
  
  const handleDataLoad = (BultosImportados, totalPeso) => {
  
    BultosImportados.forEach(bulto => {
      bulto.volumen = calcularVolumen(bulto.altura, bulto.largo, bulto.ancho);
    }); 
  
    const volumenTotalCalculado = BultosImportados.reduce((acc, bulto) => {
      const volumen = parseToNumber(bulto.volumen);
      return acc + volumen;
    }, 0);
  
  
    if (volumenTotalCalculado !== parseToNumber(totalVolumenBulto)) {
      console.log("Error:", volumenTotalCalculado, parseToNumber(totalVolumenBulto));
      setErrorMessage(`Error en el volumen: se necesita ${formatNumberWithComma(volumenTotalCalculado)} y se tiene  ${parseToNumber(totalVolumenBulto)}`);
      setShowErrorDialog(true);
    } else {
      setIsSectionVisible(false);
      setBultos(BultosImportados);
    }
  };
  

  // Agregar la función para manejar el cambio en la cantidad de bultos
  const handleCantidadBultosChange = (value) => {
    const cantidad = parseInt(value) || 0;
    setCantidadBultos(cantidad);

    // Crear nuevos bultos con la cantidad actualizada
    const bultosIniciales = Array.from({ length: cantidad }, (_, index) => ({
      bultoNumber: (index + 1).toString(),
      posiciones: [],
      peso: '0',
      altura: '0',
      largo: '0',
      ancho: '0',
      volumen: '0.000'
    }));
    
    setBultos(bultosIniciales);
    
    // Restablecer las posiciones con los items que tienen cantidad
    setPositions([...items.filter(item => parseToNumber(item.dispatchQuantity) > 0).map(item => ({
      lineId: item.lineId,
      materialCode: item.materialCode,
      description: item.description || item.product,
      dispatchQuantity: item.dispatchQuantity
    }))]);
    
    // Reiniciar los totales
    setTotalPesoBulto(0);
    setTotalVolumenBulto('0');
  };

  // Agregar la función para manejar la eliminación
  const handleDeleteMaterialFromBulto = (bultoNumber, lineId, quantity) => {
    // Restore the quantity to dispatchQuantity
    setPositions(prevPositions => 
      prevPositions.map(pos => {
        if (pos.lineId === lineId) {
          const currentDispatchQuantity = parseToNumber(pos.dispatchQuantity);
          const restoredDispatchQuantity = currentDispatchQuantity + parseToNumber(quantity);
          console.log(`Restoring quantity for line ${lineId}: ${quantity}. New dispatchQuantity: ${restoredDispatchQuantity}`);
          return {
            ...pos,
            dispatchQuantity: formatNumberWithComma(restoredDispatchQuantity),
          };
        }
        return pos;
      })
    );

    // Eliminar la posición del bulto
    setBultos(prevBultos => 
      prevBultos.map(bulto => {
        if (bulto.bultoNumber === bultoNumber) {
          return {
            ...bulto,
            posiciones: bulto.posiciones.filter(pos => pos.lineId !== lineId)
          };
        }
        return bulto;
      })
    );
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setShowViewAsnDialog(true); 
  };
  // Función para manejar el cambio en slipNumber
  const handleSlipNumberChange = (value) => {
    console.log('ñ Valor ingresado:', value); // Log the input value
    // Validar que el valor sea numérico
    if (/^\d*$/.test(value)) { // Solo permite números
      setSlipNumber(value);
      setErrorFields(prev => ({ ...prev, slipNumber: false })); // Limpiar error
      console.log('ñ Error set:', false); // Log error state
    } else {
      setSlipNumber(value); // Allow letters but set error state
      setErrorFields(prev => ({ ...prev, slipNumber: true })); // Establecer error
      console.log('ñ Error set:', true); // Log error state
    }
  };

  // Asegurarnos que las referencias estén inicializadas correctamente
  const validateFields = () => {
    const errors = [];

    // Validar campos requeridos con manejo seguro de referencias
    if (!deliveryDate) {
      errors.push('La fecha de entrega es requerida');
      deliveryDateRef.current?.focus();
    }
    if (!shipmentDate) {
      errors.push('La fecha de salida es requerida');
      shipmentDateRef.current?.focus();
    }
    if (!cargoType) {
      errors.push('El tipo de carga es requerido');
      cargoTypeRef.current?.focus();
    }
    if (!transportType) {
      errors.push('El tipo de transporte es requerido');
      transportTypeRef.current?.focus();
    }
    if (!shipmentType) {
      errors.push('El tipo de envío es requerido');
      shipmentTypeRef.current?.focus();
    }
    if (!vehiclePlate) {
      errors.push('La placa del vehículo es requerida');
      vehiclePlateRef.current?.focus();
    }
    if (!slipNumber) {
      errors.push('El número de guía es requerido');
      slipNumberRef.current?.focus();
    }

    setErrorMessages(errors);
    return errors.length === 0;
  };

  useEffect(() => {
    items.forEach(item => {
      console.log(`Material Code: ${item.materialCode}, Dispatch Quantity (value): ${item.dispatchQuantity}`);
    });
  }, [items]); // Se ejecuta cada vez que `items` cambia

  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Estados para la sección de adjuntos
  const [attachmentSections, setAttachmentSections] = useState({
    header: { isOpen: false, isRequired: false },
    safetySheet: { isOpen: false, isRequired: false },
    dispatchGuide: { isOpen: false, isRequired: true }, // Solo Guía de Despacho es requerida
    deliveryNotice: { isOpen: false, isRequired: false },
    indivisibleCertificate: { isOpen: false, isRequired: false },
    other: { isOpen: false, isRequired: false }
  });

  // Verificar si la orden está bloqueada al cargar el componente
  useEffect(() => {
    const checkOrderLocked = async () => {
      if (!purchaseOrder?.id) return;
      
      try {
        const response = await axiosInstance.get(`purchase-orders/${purchaseOrder.id}`);
        if (response.data && response.data.data && response.data.data.locked) {
          // La orden está bloqueada, redirigir al OrderDetail
          navigate(`/order-detail/${purchaseOrder.orderNumber}`);
        }
      } catch (error) {
        console.error('Error verificando estado de bloqueo:', error);
      }
    };

    checkOrderLocked();
  }, [purchaseOrder?.id, purchaseOrder?.orderNumber, navigate]);

  // Función para cargar los agendamientos pendientes
  const fetchSchedules = async () => {
    try {
      const response = await axiosInstance.get(`purchase-order-schedules/search`, {
        params: {
          orderNumber: purchaseOrder?.orderNumber,
          status: 'PENDING',
          page: 0,
          size: 10,
          sortBy: 'createdAt',
          direction: 'desc',
          hasAsnNumber: false // Add this line to filter schedules without asnNumber
        }
      });
      
      if (response.data && response.data.data) {
        setSchedules(response.data.data.data);
      }
    } catch (error) {
      console.error('Error al cargar agendamientos:', error);
    }
  };

  // Cargar agendamientos cuando se monte el componente
  useEffect(() => {
    if (purchaseOrder?.orderNumber) {
      fetchSchedules();
    }
  }, [purchaseOrder?.orderNumber]);

  // Actualizar secciones de adjuntos según tipo de transporte y carga
  useEffect(() => {
    // const isHeavyTransport = transportType && 
    //   (transportType.includes('15 Toneladas') || 
    //    transportType.includes('17 Toneladas') || 
    //    transportType.includes('28 Toneladas'));
    
    // const isChemicalCargo = cargoType === "Producto Quimico";

    setAttachmentSections(prev => ({
      ...prev,
      safetySheet: { ...prev.safetySheet, isRequired: false }, // Siempre opcional
      indivisibleCertificate: { ...prev.indivisibleCertificate, isRequired: false }, // Siempre opcional
      deliveryNotice: { ...prev.deliveryNotice, isRequired: false } // Siempre opcional
    }));
  }, [transportType, cargoType]);

  // Función para toggle de secciones
  const toggleAttachmentSection = (sectionKey) => {
    setAttachmentSections(prev => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], isOpen: !prev[sectionKey].isOpen }
    }));
  };

  // Función mejorada para manejo de archivos
  const handleAttachmentUpload = async (sectionKey, file) => {
    try {
      // Usar la función real de subida a Azure Blob Storage
      const uploadedUrl = await new Promise((resolve, reject) => {
        uploadFile(file, resolve, reject); // Pass resolve and reject to the uploader
      });
      
      setAttachments(prev => ({
        ...prev,
        [sectionKey]: {
          file: file,
          name: file.name,
          filePath: uploadedUrl, // URL real de Azure Blob Storage
          uploadDate: new Date().toISOString()
        }
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
      // Fallback: guardar sin URL por ahora (o manejar el error de forma más robusta)
      setAttachments(prev => ({
        ...prev,
        [sectionKey]: {
          file: file,
          name: file.name,
          filePath: `/attachments/${file.name}`, // URL temporal en caso de fallo
          uploadDate: new Date().toISOString()
        }
      }));
    }
  };

  // Función para remover archivos
  const removeAttachment = (sectionKey) => {
    setAttachments(prev => {
      const newAttachments = { ...prev };
      delete newAttachments[sectionKey];
      return newAttachments;
    });
  };

  // Función para manejar la selección de agendamiento
  const handleScheduleSelection = (event) => {
    const selectedId = event.target.value;
    const schedule = schedules.find(s => s.id === selectedId);
    setSelectedSchedule(schedule);
    
    if (schedule) {
      // Convertir la fecha del agendamiento al formato esperado por el DatePicker
      const scheduleDate = new Date(schedule.scheduleDate);
      const formattedDate = `${String(scheduleDate.getDate()).padStart(2, '0')}/${String(scheduleDate.getMonth() + 1).padStart(2, '0')}/${scheduleDate.getFullYear()}`;
      setDeliveryDate(formattedDate);
      setShipmentDate(formattedDate);
    }
  };

  if (isLoading) return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh' 
    }}>
      <BusyIndicator active="true" size="L" text="Cargando Notificación..." />
    </div>
  );

  return (
    <div style={{ 
      backgroundColor: '#f5f6f7', 
      minHeight: '100vh',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Navbar toggleSidebar={toggleSidebar} />
      <Breadcrumbs />

      <div style={{ 
        display: 'flex',
        flex: 1,
        marginTop: '28px',
        position: 'relative',
        width: '100%'
      }}>
        <SideMenu 
          isOpen={sidebarOpen} 
        />      
        
        <div style={{ 
          flex: 1,
          marginLeft: sidebarOpen ? '250px' : '0',
          transition: 'all 0.3s ease',
          padding: '20px',
          width: '100%',
          maxWidth: sidebarOpen ? 'calc(100% - 250px)' : '100%',
          overflowX: 'hidden',
          overflowY: 'auto'
        }}>


          
          <div style={{ 
            width: '100%',
            maxWidth: sidebarOpen ? '1200px' : '100%',
            margin: '0 auto',
            transition: 'max-width 0.3s ease'
          }}>
            <Wizard 
              onStepChange={handleStepChange} 
              style={{ 
                height: 'calc(100vh - 88px)',
                width: '100%'
              }}
            >
              {/* Paso 1: Informe de la obra de compra */}
              <WizardStep icon="document" titleText={autoT('Informe Orden de compra')} selected={selectedStep === 0}>
                <div style={{ padding: '1rem' }}>
                  {/* Orden de compra y contrato */}
                  <div style={{ marginBottom: '2rem' }}>
                    <Form titleText={autoT('Información Principal')} style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 0 4px rgba(0,0,0,0.1)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label>{autoT('Orden de compra:')}</Label>
                          <Text>{purchaseOrder?.orderNumber}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label>{autoT('Proveedor:')}</Label>
                          <Text>{purchaseOrder?.supplier?.supplierName}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label>{autoT('Incoterm:')}</Label>
                          <Text>{purchaseOrder?.incoterm}</Text>
                        </div>
                      </div>
                    </Form>
                  </div>

                  {/* Cabecera Notificación */}
                  <Title level="H3" style={{ marginBottom: '1rem' }}>{autoT('Cabecera Notificación')}</Title>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                    {/* Datos Básicos */}
                    <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 0 4px rgba(0,0,0,0.1)' }}>
                      <Title level="H5" style={{ marginBottom: '1rem' }}>{autoT('Datos Básicos')}</Title>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Descripción:')}</Label>
                          <TextArea 
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ingrese descripción" 
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }} 
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Cantidad de bultos:')}</Label>
                          <Input
                            type="Number" 
                            value={cantidadBultos}
                            onChange={(e) => handleCantidadBultosChange(e.target.value)}
                            ref={cantidadBultosRef}
                            placeholder="Ingrese cantidad" 
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }} 
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Volumen de bulto - m³:')}</Label>
                          <Input 
                            type="text" 
                            value={totalVolumenBulto}
                            ref={totalVolumenRef}
                            onChange={handleTotalVolumenChange}
                            onBlur={handleTotalVolumenBlur}
                            placeholder="Ingrese volumen total" 
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }} 
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Peso de bulto - kg:')}</Label>
                          <Input 
                            ref={totalPesoRef}
                            type="text" 
                            value={totalPesoBulto}
                            onChange={handleTotalPesoChange}
                            onBlur={handleTotalPesoBlur}
                            placeholder="Ingrese peso total" 
                            style={{ 
                              flex: 1, 
                              maxWidth: '80%', 
                              marginLeft: '1rem', 
                              borderColor: errorFields.totalPeso ? 'red' : 'initial'
                            }} 
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Label style={{ marginBottom: '0.5rem' }}>{autoT('Instrucciones de despacho:')}</Label>
                            <TextArea 
                              placeholder="Ingrese instrucciones" 
                              style={{ maxWidth: '80%', marginLeft: '1rem' }} 
                              value={shipmentInstructions} 
                              ref={shipmentInstructionsRef}
                              onChange={(e) => setShipmentInstructions(e.target.value)} // Manejador de cambio
                            />
                          </div>
                      </div>
                    </div>

                    {/* Condición de entrega */}
                    <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 0 4px rgba(0,0,0,0.1)' }}>
                      <Title level="H5" style={{ marginBottom: '1rem' }}>{autoT('Condición de entrega')}</Title>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Label style={{ marginBottom: '0.5rem' }}>{autoT('Asignar agendamiento')}</Label>
                          <Select
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }}
                            onChange={handleScheduleSelection}
                            value={selectedSchedule?.id || ''}
                          >
                            <Option value="">{autoT('Seleccionar agendamiento')}</Option>
                            {schedules.map(schedule => (
                              <Option key={schedule.id} value={schedule.id}>
                                {`Agendamiento ${schedule.appoimentNumber} - ${new Date(schedule.scheduleDate).toLocaleString()}`}
                              </Option>
                            ))}
                          </Select>
                          </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Label style={{ marginBottom: '0.5rem' }}>{autoT('Fecha de salida:')}</Label>
                        <DatePicker 
                          ref={shipmentDateRef}
                          value={shipmentDate || getDateOnly(new Date())}
                          onChange={handleShipmentDateChange}
                          formatPattern="dd/MM/yyyy"
                          style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }}
                        />
                      </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Fecha de entrega:')}</Label>
                          <DatePicker 
                            ref={deliveryDateRef}
                            value={deliveryDate || getDateOnly(new Date())}
                            onChange={handleDeliveryDateChange}
                            formatPattern="dd/MM/yyyy"
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Tipo de carga:')}</Label>
                          <Select 
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }}
                            onChange={(e) => setCargoType(e.target.value)}
                            value={cargoType}
                            ref={cargoTypeRef}
                          >
                            <Option value="">{autoT('Seleccionar')}</Option>
                            <Option value="Carga General">{autoT('Carga General')}</Option>
                            <Option value="Producto Quimico">{autoT('Producto Quimico')}</Option>
                            <Option value="Sustancias Peligrosas">{autoT('Sustancias Peligrosas')}</Option>
                          </Select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ marginBottom: '0.5rem' }}>{autoT('Tipo de transporte:')}</Label>
                          <Select 
                            style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }}
                            onChange={(e) => setTransportType(e.target.value)}
                            value={transportType}
                            ref={transportTypeRef}
                          >
                            <Option value="">{autoT('Seleccionar')}</Option>
                            <Option value="Camioneta">{autoT('Camioneta')}</Option>
                            <Option value="Furgon o Utilitario">{autoT('Furgon o Utilitario')}</Option>
                            <Option value="Camion menor a 1 Tonelada">{autoT('Equipo 3/4')}</Option>
                            <Option value="Camion 1,5 Toneladas">{autoT('Equipo 1,5 Toneladas')}</Option>
                            <Option value="Camion 3,5 Toneladas">{autoT('Equipo 3,5 Toneladas')}</Option>
                            <Option value="Camion 15 Toneladas">{autoT('Equipo 15 Toneladas')}</Option>
                            <Option value="Camion 17 Toneladas">{autoT('Equipo 17 Toneladas')}</Option>
                            <Option value="Camion 28 Toneladas">{autoT('Equipo 28 Toneladas')}</Option>
                          </Select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Label style={{ marginBottom: '0.5rem' }}>{autoT('Tipo de bulto:')}</Label>
                            <Select 
                              style={{ flex: 1, maxWidth: '80%', marginLeft: '1rem' }} 
                              value={shipmentType} 
                              ref={shipmentTypeRef}
                              onChange={(e) => setShipmentType(e.target.value)} // Asegúrate de que esto esté presente
                            >
                              <Option value="">{autoT('Seleccionar')}</Option>
                              <Option value="A granel">{autoT('A granel')}</Option>
                              <Option value="Caja Carton">{autoT('Caja cartón')}</Option>
                              <Option value="con Antigolpes">{autoT('Con antigolpes')}</Option>
                              <Option value="Caja de Madera con Antigolpes">{autoT('Caja de madera con antigolpes')}</Option>
                              <Option value="Embalaje con marca Fragil">{autoT('Embalaje con marca "Frágil"')}</Option>
                              <Option value="Embalaje con marca No Mojar">{autoT('Embalaje con marca "No Mojar"')}</Option>
                              <Option value="Embalaje con marca Peligroso">{autoT('Embalaje con marca "Peligroso"')}</Option>
                              <Option value="Embalaje estandar del Proveedor">{autoT('Embalaje estándar del proveedor')}</Option>
                              <Option value="Embalaje Impermeable">{autoT('Embalaje impermeable')}</Option>
                              <Option value="Embalaje de Exportacion">{autoT('Embalaje de exportación')}</Option>
                              <Option value="Jaba de Madera">{autoT('Jaba de madera')}</Option>
                              <Option value="Embalaje ver Clausula">{autoT('Embalaje ver cláusula')}</Option>
                              <Option value="Caja de Madera">{autoT('Caja de madera')}</Option>
                              <Option value="Marcado Mantener Aislado">{autoT('Marcado "Mantener Aislado"')}</Option>
                              <Option value="Pallet">{autoT('Pallet')}</Option>
                              <Option value="Container">{autoT('Container')}</Option>
                            </Select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Label style={{ marginBottom: '0.5rem' }}>{autoT('Placa patente:')}</Label>
                            <TextArea 
                              placeholder="Ingrese placa patente" 
                              style={{ maxWidth: '80%', marginLeft: '1rem' }} 
                              value={vehiclePlate} 
                              ref={vehiclePlateRef} 
                              onChange={(e) => setVehiclePlate(e.target.value)} // Manejador de cambio
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Label style={{ marginBottom: '0.5rem' }}>{autoT('Guía de despacho:')}</Label>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: '80%', marginLeft: '1rem', position: 'relative' }}>
                              <TextArea 
                                placeholder="Ingrese guía de despacho" 
                                style={{ 
                                  width: '100%', // Make TextArea take full width of its container
                                  borderColor: errorFields.slipNumber ? 'red' : 'initial', // Keep border red as a fallback/additional indicator
                                  color: errorFields.slipNumber ? 'red' : 'initial', // Text color to red
                                  paddingRight: errorFields.slipNumber ? '2rem' : '0.5rem' // Add padding for the icon
                                }} 
                                value={slipNumber} 
                                ref={slipNumberRef} 
                                onChange={(e) => handleSlipNumberChange(e.target.value)}
                                type="number"
                              />
                              {errorFields.slipNumber && (
                                <ui5-icon 
                                  name="message-warning" 
                                  style={{ 
                                    color: 'red', 
                                    position: 'absolute', 
                                    right: '0.5rem', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)'
                                  }}
                                ></ui5-icon>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Footer con botones */}
                <div style={{ 
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'white',
                  padding: '1rem',
                  marginTop: '2rem',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                  borderTop: '1px solid #e5e5e5'
                }}>
                  <Button 
                    design="Emphasized"
                    onClick={() => setSelectedStep(1)}
                    icon="arrow-right"
                  >
                    {autoT('Siguiente')}
                  </Button>
                </div>
              </WizardStep>

              {/* Paso 2: Item ASN */}
              <WizardStep
                icon="product"
                titleText={autoT('Item Notificación')}
                selected={selectedStep === 1}
              >
             {isSectionVisible && (

                <div style={{ padding: '1rem' }}>
                  <Title level="H2" style={{ marginBottom: '1rem' }}>{autoT('Item Notificación')}</Title>
                  
                  {/* Tabla principal de items */}
                  <div style={{ marginBottom: '2rem' }}>
                    <AnalyticalTable
                      data={purchaseOrder.items || []}
                      columns={itemColumns}
                      selectionMode="None"
                      minRows={items?.length || 0}
                      visibleRows={items?.length || 0}
                      scaleWidthMode="Smart"
                      columnConfig={{
                        quantity: {
                          hAlign: "End"
                        },
                        quantityReceived: {
                          hAlign: "End"
                        },
                        quantitySent: {
                          hAlign: "End"
                        },
                        quantityPending: {
                          hAlign: "End"
                        }
                      }}
                    />
                  </div>
                </div>
                             )}

                <div style={{ 
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'white',
                  padding: '1rem',
                  marginTop: '2rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                  borderTop: '1px solid #e5e5e5'
                }}>
                  <Button 
                    onClick={() => setSelectedStep(0)}
                    icon="arrow-left"
                  >
                    {autoT('Anterior')}
                  </Button>
                  <Button 
                    design="Emphasized"
                    onClick={() => setSelectedStep(2)}
                    icon="arrow-right"
                  >
                    {autoT('Siguiente')}
                  </Button>
                </div>

              </WizardStep>

              {/* Paso 3: Packing List */}
              <WizardStep icon="document" titleText={autoT('Packing List')} selected={selectedStep === 2}>
                <div style={{ padding: '1rem' }}>
                  {/* Contenedor para ambas tablas */}
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr', // Divide el espacio en dos columnas iguales
                    gap: '2rem',
                    marginBottom: '2rem'
                  }}>
                    
                    {/* Sección de Posiciones */}
                    {isSectionVisible && (
                    <div style={{ 
                      backgroundColor: 'white',
                      padding: '1rem',
                      borderRadius: '8px',
                      boxShadow: '0 0 4px rgba(0,0,0,0.1)'
                    }}>
                      <Title level="H3" style={{ marginBottom: '1rem' }}>{autoT('Posiciones de materiales')}</Title>
                      <div style={{ marginBottom: '1rem' }}>
                        <Button 
                          design="Emphasized" 
                          onClick={handleLoadPositions}
                          style={{ marginRight: '1rem' }}
                        >
                          {autoT('Cargar Posiciones de materiales')}
                        </Button>
                      </div>
                      <AnalyticalTable
                        data={positions}
                        columns={positionColumns}
                        selectionMode="None"
                        minRows={positions.length}
                        visibleRows={positions.length}
                        scaleWidthMode="Smart"
                        style={{ height: '400px' }} // Altura fija para mantener consistencia
                      />
                    </div>
                    )}

                    {/* Sección de Bultos */}
                    <div style={{ 
                      backgroundColor: 'white',
                      padding: '1rem',
                      borderRadius: '8px',
                      boxShadow: '0 0 4px rgba(0,0,0,0.1)'
                    }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '1rem' 
                    }}>
                        <Title level="H3">{autoT('Dimensiones de los Bultos')}</Title>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div>
                            {/* Botón para abrir el popup */}
                            <Button icon="full-screen" onClick={openModal}></Button>

                            {/* Modal con el contenido de los bultos */}
                            <Dialog open={isOpen} onAfterClose={closeModal}>
                              <div style={{ backgroundColor: 'white', padding: '0rem', borderRadius: '1px', boxShadow: '0 0 4px rgba(0,0,0,0.1)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                  <Title level="H3">{autoT('Dimensiones de los Bultos')}</Title>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <ImportBultos onDataLoad={handleDataLoad} cantidadBultos={cantidadBultos} totalPesoBulto={totalPesoBulto}/>

                                    <Button
                                      icon="refresh"
                                      design="Transparent"
                                      onClick={handleClearBultos}
                                    >
                                      {autoT('Limpiar Bulto')}
                                    </Button>
                                  </div>
                                </div>

                                <div style={{ backgroundColor: '#f5f6f7', padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px' }}>
                                  <Text>Peso bruto total: {formatNumberWithComma(totalPesoBulto)} kg</Text>
                                  <Text>Volumen total: {calculateAndDisplayTotalVolume()} m³</Text>
                                </div>

                                    {/* Aquí puedes mantener la tabla de Bultos y demás contenido */}
                                    <div style={{ maxHeight: '500px', overflowY: 'auto', marginBottom: '1rem' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left' }}>{autoT('Nro Bulto')}</th>
                                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Peso - kg')}</th>
                                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Altura - metros')}</th>
                                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Largo - metros')}</th>
                                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Ancho - metros')}</th>
                                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Volumen - m³')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {bultos.map((bulto) => (
                                          <tr key={bulto.bultoNumber}>
                                            <td style={{ padding: '8px', border: '1px solid #eee' }}>{autoT('Bulto {{bultoNumber}}', { bultoNumber: bulto.bultoNumber })}</td>
                                            <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                              <input
                                                key={`peso-${bulto.bultoNumber}-${bulto.peso}`}
                                                type="text"
                                                defaultValue={bulto.peso === '0' || bulto.peso === '0,000' ? '' : formatNumberWithComma(bulto.peso)}
                                                placeholder={bulto.peso === '0' || bulto.peso === '0,000' ? autoT('Ingrese peso') : ''}
                                                onChange={(e) => {
                                                  // No actualizar el estado principal en cada tecla para permitir escritura libre
                                                }}
                                                onFocus={(e) => {
                                                  if (e.target && e.target.select) e.target.select();
                                                }}
                                                onBlur={(e) => {
                                                  let newValue = e.target.value.replace(/[^0-9,.]/g, '');
                                                  handleBultoUpdate(bulto.bultoNumber, 'peso', parseToNumber(newValue));
                                                }}
                                                style={{
                                                  width: '80px',
                                                  textAlign: 'right',
                                                  border: 'none',
                                                  padding: '4px',
                                                  fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                                  fontSize: '14px',
                                                  boxSizing: 'border-box',
                                                  backgroundColor: 'transparent'
                                                }}
                                                ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-peso`] = el}
                                                onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'peso')}
                                              />
                                            </td>
                                            <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                              <input
                                                key={`altura-${bulto.bultoNumber}-${bulto.altura}`}
                                                type="text"
                                                defaultValue={bulto.altura === '0' || bulto.altura === '0,000' ? '' : formatNumberWithComma(bulto.altura)}
                                                placeholder={bulto.altura === '0' || bulto.altura === '0,000' ? autoT('Ingrese altura') : ''}
                                                onChange={(e) => {
                                                  // No actualizar el estado principal en cada tecla para permitir escritura libre
                                                }}
                                                onFocus={(e) => {
                                                  if (e.target && e.target.select) e.target.select();
                                                }}
                                                onBlur={(e) => {
                                                  console.log(`onBlur - original e.target.value: ${e.target.value}`);
                                                  let newValue = e.target.value.replace(/[^0-9,.]/g, ''); // Sanitizar al salir del foco
                                                  console.log(`onBlur - newValue after sanitization: ${newValue}`);
                                                  const parsedValue = parseToNumber(newValue);
                                                  console.log(`onBlur - parsedValue after parseToNumber: ${parsedValue}`);
                                                  handleBultoUpdate(bulto.bultoNumber, 'altura', parsedValue); // Actualizar estado principal con el número parseado
                                                }}
                                                style={{
                                                  width: '80px',
                                                  textAlign: 'right',
                                                  border: 'none', // Remover borde del input
                                                  padding: '4px',
                                                  fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                                  fontSize: '14px',
                                                  boxSizing: 'border-box',
                                                  backgroundColor: 'transparent'
                                                }}
                                                ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-altura`] = el}
                                                onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'altura')}
                                              />
                                            </td>
                                            <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                              <input
                                                key={`largo-${bulto.bultoNumber}-${bulto.largo}`}
                                                type="text"
                                                defaultValue={bulto.largo === '0' || bulto.largo === '0,000' ? '' : formatNumberWithComma(bulto.largo)}
                                                placeholder={bulto.largo === '0' || bulto.largo === '0,000' ? autoT('Ingrese largo') : ''}
                                                onChange={(e) => {
                                                  // No actualizar el estado principal en cada tecla para permitir escritura libre
                                                }}
                                                onFocus={(e) => {
                                                  if (e.target && e.target.select) e.target.select();
                                                }}
                                                onBlur={(e) => {
                                                  let newValue = e.target.value.replace(/[^0-9,.]/g, '');
                                                  const parsedValue = parseToNumber(newValue);
                                                  handleBultoUpdate(bulto.bultoNumber, 'largo', parsedValue);
                                                }}
                                                style={{
                                                  width: '80px',
                                                  textAlign: 'right',
                                                  border: 'none',
                                                  padding: '4px',
                                                  fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                                  fontSize: '14px',
                                                  boxSizing: 'border-box',
                                                  backgroundColor: 'transparent'
                                                }}
                                                ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-largo`] = el}
                                                onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'largo')}
                                              />
                                            </td>
                                            <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                              <input
                                                key={`ancho-${bulto.bultoNumber}-${bulto.ancho}`}
                                                type="text"
                                                defaultValue={bulto.ancho === '0' || bulto.ancho === '0,000' ? '' : formatNumberWithComma(bulto.ancho)}
                                                placeholder={bulto.ancho === '0' || bulto.ancho === '0,000' ? autoT('Ingrese ancho') : ''}
                                                onChange={(e) => {
                                                  // No actualizar el estado principal en cada tecla para permitir escritura libre
                                                }}
                                                onFocus={(e) => {
                                                  if (e.target && e.target.select) e.target.select();
                                                }}
                                                onBlur={(e) => {
                                                  let newValue = e.target.value.replace(/[^0-9,.]/g, '');
                                                  const parsedValue = parseToNumber(newValue);
                                                  handleBultoUpdate(bulto.bultoNumber, 'ancho', parsedValue);
                                                }}
                                                style={{
                                                  width: '80px',
                                                  textAlign: 'right',
                                                  border: 'none',
                                                  padding: '4px',
                                                  fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                                  fontSize: '14px',
                                                  boxSizing: 'border-box',
                                                  backgroundColor: 'transparent'
                                                }}
                                                ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-ancho`] = el}
                                                onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'ancho')}
                                              />
                                            </td>
                                            <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                              <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(bulto.volumen)}</Text>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    </div>
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                                        <Button onClick={closeModal} style={{ backgroundColor: 'red', color: 'white' }}>{autoT('Cerrar')}</Button>
                                      </div>
                                          </div>
                                      </Dialog>
                                    </div>
                          <ImportBultos onDataLoad={handleDataLoad} cantidadBultos={cantidadBultos} totalPesoBulto={totalPesoBulto}/>
                            <Button
                                icon="refresh"
                                design="Transparent"
                                onClick={handleClearBultos}
                            >
                                {autoT('Limpiar Bulto')}
                            </Button>
                        </div>
                    </div>
                      <div style={{ 
                        backgroundColor: '#f5f6f7',
                        padding: '0.5rem',
                        marginBottom: '1rem',
                        borderRadius: '4px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Text>{autoT('Peso bruto total')}: {formatNumberWithComma(totalPesoTabla)}</Text>
                          <Text>/ {formatNumberWithComma(totalPesoBulto)} kg</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Text>{autoT('Volumen total')}: {formatNumberWithComma(totalVolumenTabla)}</Text>
                        <Text>/ {formatNumberWithComma(totalVolumenBulto)} m³</Text> 
                        </div>

                      </div>
                      
                      {/* Tabla de Bultos (cabecera) */}
                      <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '1rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left' }}>{autoT('Nro Bulto')}</th>
                            <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Peso - kg')}</th>
                            <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Altura - metros')}</th>
                            <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Largo - metros')}</th>
                            <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Ancho - metros')}</th>
                            <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right' }}>{autoT('Volumen - m³')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bultos.map((bulto) => (
                            <tr key={bulto.bultoNumber}>
                              <td style={{ padding: '8px', border: '1px solid #eee' }}>{autoT('Bulto {{bultoNumber}}', { bultoNumber: bulto.bultoNumber })}</td>
                              <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                <input
                                  key={`peso-${bulto.bultoNumber}-${bulto.peso}`}
                                  type="text"
                                  defaultValue={bulto.peso === '0' || bulto.peso === '0,000' ? '' : formatNumberWithComma(bulto.peso)}
                                  placeholder={bulto.peso === '0' || bulto.peso === '0,000' ? autoT('Ingrese peso') : ''}
                                  onChange={(e) => {
                                    // No actualizar el estado principal en cada tecla para permitir escritura libre
                                  }}
                                  onFocus={(e) => {
                                    if (e.target && e.target.select) e.target.select();
                                  }}
                                  onBlur={(e) => {
                                    console.log(`onBlur - original e.target.value: ${e.target.value}`);
                                    let newValue = e.target.value.replace(/[^0-9,.]/g, ''); // Sanitizar al salir del foco
                                    console.log(`onBlur - newValue after sanitization: ${newValue}`);
                                    const parsedValue = parseToNumber(newValue);
                                    console.log(`onBlur - parsedValue after parseToNumber: ${parsedValue}`);
                                    handleBultoUpdate(bulto.bultoNumber, 'peso', parsedValue); // Actualizar estado principal con el número parseado
                                  }}
                                  style={{
                                    width: '80px',
                                    textAlign: 'right',
                                    border: 'none', // Remover borde del input
                                    padding: '4px',
                                    fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                    fontSize: '14px',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent' // Fondo transparente para que el td se vea
                                  }}
                                  ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-peso`] = el}
                                  onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'peso')}
                                />
                              </td>
                              <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                <input
                                  key={`altura-${bulto.bultoNumber}-${bulto.altura}`}
                                  type="text"
                                  defaultValue={bulto.altura === '0' || bulto.altura === '0,000' ? '' : formatNumberWithComma(bulto.altura)}
                                  placeholder={bulto.altura === '0' || bulto.altura === '0,000' ? autoT('Ingrese altura') : ''}
                                  onChange={(e) => {
                                    // No actualizar el estado principal en cada tecla para permitir escritura libre
                                  }}
                                  onFocus={(e) => {
                                    if (e.target && e.target.select) e.target.select();
                                  }}
                                  onBlur={(e) => {
                                    console.log(`onBlur - original e.target.value: ${e.target.value}`);
                                    let newValue = e.target.value.replace(/[^0-9,.]/g, ''); // Sanitizar al salir del foco
                                    console.log(`onBlur - newValue after sanitization: ${newValue}`);
                                    const parsedValue = parseToNumber(newValue);
                                    console.log(`onBlur - parsedValue after parseToNumber: ${parsedValue}`);
                                    handleBultoUpdate(bulto.bultoNumber, 'altura', parsedValue); // Actualizar estado principal con el número parseado
                                  }}
                                  style={{
                                    width: '80px',
                                    textAlign: 'right',
                                    border: 'none', // Remover borde del input
                                    padding: '4px',
                                    fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                    fontSize: '14px',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent'
                                  }}
                                  ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-altura`] = el}
                                  onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'altura')}
                                />
                              </td>
                              <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                <input
                                  key={`largo-${bulto.bultoNumber}-${bulto.largo}`}
                                  type="text"
                                  defaultValue={bulto.largo === '0' || bulto.largo === '0,000' ? '' : formatNumberWithComma(bulto.largo)}
                                  placeholder={bulto.largo === '0' || bulto.largo === '0,000' ? autoT('Ingrese largo') : ''}
                                  onChange={(e) => {
                                    // No actualizar el estado principal en cada tecla para permitir escritura libre
                                  }}
                                  onFocus={(e) => {
                                    if (e.target && e.target.select) e.target.select();
                                  }}
                                  onBlur={(e) => {
                                    let newValue = e.target.value.replace(/[^0-9,.]/g, '');
                                    const parsedValue = parseToNumber(newValue);
                                    handleBultoUpdate(bulto.bultoNumber, 'largo', parsedValue);
                                  }}
                                  style={{
                                    width: '80px',
                                    textAlign: 'right',
                                    border: 'none',
                                    padding: '4px',
                                    fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                    fontSize: '14px',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent'
                                  }}
                                  ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-largo`] = el}
                                  onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'largo')}
                                />
                              </td>
                              <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                <input
                                  key={`ancho-${bulto.bultoNumber}-${bulto.ancho}`}
                                  type="text"
                                  defaultValue={bulto.ancho === '0' || bulto.ancho === '0,000' ? '' : formatNumberWithComma(bulto.ancho)}
                                  placeholder={bulto.ancho === '0' || bulto.ancho === '0,000' ? autoT('Ingrese ancho') : ''}
                                  onChange={(e) => {
                                    // No actualizar el estado principal en cada tecla para permitir escritura libre
                                  }}
                                  onFocus={(e) => {
                                    if (e.target && e.target.select) e.target.select();
                                  }}
                                  onBlur={(e) => {
                                    let newValue = e.target.value.replace(/[^0-9,.]/g, '');
                                    const parsedValue = parseToNumber(newValue);
                                    handleBultoUpdate(bulto.bultoNumber, 'ancho', parsedValue);
                                  }}
                                  style={{
                                    width: '80px',
                                    textAlign: 'right',
                                    border: 'none',
                                    padding: '4px',
                                    fontFamily: "'72', '72full', Arial, Helvetica, sans-serif",
                                    fontSize: '14px',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent'
                                  }}
                                  ref={el => bultoInputRefs.current[`${bulto.bultoNumber}-ancho`] = el}
                                  onKeyDown={(e) => handleBultoKeyDown(e, bulto.bultoNumber, 'ancho')}
                                />
                              </td>
                              <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                                <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(bulto.volumen)}</Text>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                      
                      {/* Nueva tabla de materiales por bulto */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginTop: '1rem' }}>
                        <Title level="H4">{autoT('Materiales por Bulto')}</Title>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Button icon="full-screen" onClick={openExpandedModal}></Button>
                          <ExportExcelButton data={getBultoTableData(bultos)} />                 
                          <ImportExcelButton onDataImport={handleDataImport} />
                        </div>
                      </div>
                      <AnalyticalTable
                        data={getBultoTableData(bultos)}
                        columns={[
                          { Header: `${autoT('Nro. Bulto')}`, accessor: 'bultoNumber', Cell: ({ value }) => `${autoT('Bulto')} ${value}` },
                          { Header: `${autoT('Posición')}`, accessor: 'lineId' },
                          { Header: `${autoT('Material')}`, accessor: 'materialCode' },
                          { Header: `${autoT('Descripción')}`, accessor: 'description' },
                          { Header: `${autoT('Cantidad')}`, accessor: 'quantity', Cell: ({ value})  => formatNumberWithComma(value)},
                          { 
                            Header: `${autoT('Eliminar')}`, 
                            accessor: 'actions',
                            Cell: ({ row }) => (
                              <Button
                                icon="delete"
                                design="Transparent"
                                onClick={() => handleDeleteMaterialFromBulto(
                                  row.original.bultoNumber,
                                  row.original.lineId,
                                  row.original.quantity
                                )}
                                style={{ color: 'red' }}
                              />
                            )
                          }
                        ]}
                        selectionMode="None"
                        minRows={getBultoTableData(bultos).length || 1}
                        visibleRows={getBultoTableData(bultos).length || 1}
                        scaleWidthMode="Smart"
                        style={{ height: '200px' }}
                      />
                    </div>
                  </div>
                  <Dialog
                    open={isExpandedOpen}
                    onAfterClose={closeExpandedModal}
                    style={{ width: '90vw', maxWidth: '1200px', maxHeight: '80vh' }} // Ajusta el tamaño del modal
                  >                      
                    {/* Título y Botón de Cerrar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <Title level="H3">{autoT('Materiales por Bulto')}</Title>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                          <ExportExcelButton data={getBultoTableData(bultos)} />                 
                          <ImportExcelButton onDataImport={handleDataImport} />
                        </div>
                    </div>

                    <AnalyticalTable
                      data={getBultoTableData(bultos)}
                      columns={[
                        { Header: 'Nro. Bulto', accessor: 'bultoNumber', Cell: ({ value }) => `${autoT('Bulto')} ${value}` },
                        { Header: 'Posición', accessor: 'lineId' },
                        { Header: 'Material', accessor: 'materialCode' },
                        { Header: 'Descripción', accessor: 'description' },
                        { Header: 'Cantidad', accessor: 'quantity', Cell: ({ value})  => formatNumberWithComma(value) },
                        { 
                          Header: 'Eliminar', 
                          accessor: 'actions',
                          Cell: ({ row }) => (
                            <Button
                              icon="delete"
                              design="Transparent"
                              onClick={() => handleDeleteMaterialFromBulto(
                                row.original.bultoNumber,
                                row.original.lineId,
                                row.original.quantity
                              )}
                              style={{ color: 'red' }}
                            />
                          )
                        }
                      ]}
                      selectionMode="None"
                      minRows={getBultoTableData(bultos).length || 1}
                      visibleRows={getBultoTableData(bultos).length || 1}
                      scaleWidthMode="Smart"
                      style={{ height: '500px' }} // Altura ajustada para ser igual al primer diálogo
                    />
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                      <Button onClick={closeExpandedModal} design="Transparent" style={{ backgroundColor: 'red', color: 'white' }}
                      >{autoT('Cerrar')}</Button>
                    </div>
                  </Dialog>
                  {/* Botones de navegación */}
                  <div style={{ 
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    padding: '1rem',
                    marginTop: '2rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    borderTop: '1px solid #e5e5e5'
                  }}>
                    <Button 
                      onClick={() => setSelectedStep(1)}
                      icon="arrow-left"
                    >
                      {autoT('Anterior')}
                    </Button>
                    <Button 
                      design="Emphasized"
                      onClick={() => setSelectedStep(3)}
                      icon="arrow-right"
                    >
                      {autoT('Siguiente')}
                    </Button>
                  </div>
                </div>
              </WizardStep>

              {/* Paso 4: Adjuntos */}
              <WizardStep
                icon="attachment"
                titleText={autoT('Adjuntos')}
                selected={selectedStep === 3}
              >
                <div style={{ padding: '1rem' }}>
                  <Title level="H2" style={{ marginBottom: '1rem' }}>{autoT('Adjuntos')}</Title>
                  
                  {/* Sección de Adjuntos Rediseñada */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    
                    {/* Cabecera ASN */}
                          <div style={{
                            backgroundColor: '#fbbf24',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            width: 'fit-content' // Limit width to content
                          }}>
                            {Object.keys(attachments).length}/1 requeridos
                    </div>

                    {/* Plantilla de Seguridad */}
                    <div style={{ 
                      border: `1px solid ${attachmentSections.safetySheet?.isRequired ? '#ef4444' : '#3b82f6'}`, 
                      borderRadius: '8px', 
                      backgroundColor: 'white',
                      overflow: 'hidden'
                    }}>
                      <div 
                        onClick={() => toggleAttachmentSection('safetySheet')}
                        style={{ 
                          padding: '1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: attachments.safetySheet ? '#10b981' : (attachmentSections.safetySheet?.isRequired ? '#fef2f2' : '#eff6ff')
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            {attachments.safetySheet ? <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> : <ui5-icon name="attachment"></ui5-icon>} {autoT('Adjunta plantilla de seguridad')}
                          </Text>
                          {attachments.safetySheet ? (
                            <div style={{
                              backgroundColor: '#10b981',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {autoT('Completado')}
                            </div>
                          ) : (
                            <div style={{
                              backgroundColor: attachmentSections.safetySheet?.isRequired ? '#ef4444' : '#3b82f6',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {attachmentSections.safetySheet?.isRequired ? autoT('Requerido') : autoT('Opcional')}
                            </div>
                          )}
                        </div>
                        <Text>{attachmentSections.safetySheet?.isOpen ? '▲' : '▼'}</Text>
                      </div>
                      
                      {attachmentSections.safetySheet?.isOpen && (
                        <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                          {attachments.safetySheet ? (
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '1rem',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #10b981',
                              borderRadius: '6px'
                            }}>
                              <div>
                                <Text style={{ fontWeight: 'bold', color: '#059669' }}> <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> {autoT('Archivo adjuntado')}</Text>
                                <Text style={{ fontSize: '14px', color: '#6b7280' }}>{attachments.safetySheet.name || attachments.safetySheet.file?.name}</Text>
                              </div>
                              <Button 
                                design="Transparent" 
                                icon="delete"
                                onClick={() => removeAttachment('safetySheet')}
                                style={{ color: '#ef4444' }}
                              >
                                {autoT('Remover')}
                              </Button>
                            </div>
                          ) : (
                            <div style={{
                              border: '2px dashed #d1d5db',
                              borderRadius: '8px',
                              padding: '2rem',
                              textAlign: 'center',
                              backgroundColor: '#fafafa'
                            }}>
                              <div style={{ marginBottom: '1rem' }}>
                              <ui5-icon name="folder-full" style={{ width: '3rem', height: '3rem', color: 'orange'}}></ui5-icon>
                              </div>
                              <Text style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                                {autoT('Arrastra y suelta tu archivo aquí')}
                              </Text>
                              <Text style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                                {autoT('o selecciona un archivo')}
                              </Text>
                              <div style={{ 
                                backgroundColor: '#eff6ff', 
                                padding: '0.5rem', 
                                borderRadius: '4px', 
                                marginBottom: '1rem',
                                border: '1px solid #3b82f6'
                              }}>
                                <Text style={{ fontSize: '12px', color: '#1d4ed8' }}>
                                  {autoT('Formatos soportados: PDF, DOC, XLS, JPG, PNG (Máx. 10MB)')}
                                </Text>
                              </div>
                              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                <Button design="Transparent">{autoT('Cancelar')}</Button>
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                  onChange={async (e) => {
                                    if (e.target.files[0]) {
                                      await handleAttachmentUpload('safetySheet', e.target.files[0]);
                                    }
                                  }}
                                  style={{ display: 'none' }}
                                  id="safetySheet-upload"
                                />
                                <Button 
                                  design="Emphasized"
                                  onClick={() => document.getElementById('safetySheet-upload').click()}
                                >
                                  {autoT('Seleccionar archivo')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Guía de Despacho */}
                    <div style={{ 
                      border: '1px solid #ef4444', 
                      borderRadius: '8px', 
                      backgroundColor: 'white',
                      overflow: 'hidden'
                    }}>
                      <div 
                        onClick={() => toggleAttachmentSection('dispatchGuide')}
                        style={{ 
                          padding: '1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: attachments.dispatchGuide ? '#10b981' : '#fef2f2'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            {attachments.dispatchGuide ? <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> : <ui5-icon name="attachment"></ui5-icon>} {autoT('Adjunta guía despacho')}
                          </Text>
                          {attachments.dispatchGuide ? (
                            <div style={{
                              backgroundColor: '#10b981',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px', 
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {autoT('Completado')}
                            </div>
                          ) : (
                            <div style={{
                              backgroundColor: '#ef4444',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {autoT('Requerido')}
                            </div>
                          )}
                        </div>
                        <Text>{attachmentSections.dispatchGuide?.isOpen ? '▲' : '▼'}</Text>
                      </div>
                      
                      {attachmentSections.dispatchGuide?.isOpen && (
                        <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                          {attachments.dispatchGuide ? (
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '1rem',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #10b981',
                              borderRadius: '6px'
                            }}>
                              <div>
                                <Text style={{ fontWeight: 'bold', color: '#059669' }}> <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> {autoT('Archivo adjuntado')}</Text>
                                <Text style={{ fontSize: '14px', color: '#6b7280' }}>{attachments.dispatchGuide.name || attachments.dispatchGuide.file?.name}</Text>
                              </div>
                              <Button 
                                design="Transparent" 
                                icon="delete"
                                onClick={() => removeAttachment('dispatchGuide')}
                                style={{ color: '#ef4444' }}
                              >
                                {autoT('Remover')}
                              </Button>
                            </div>
                          ) : (
                            <div style={{
                              border: '2px dashed #d1d5db',
                              borderRadius: '8px',
                              padding: '2rem',
                              textAlign: 'center',
                              backgroundColor: '#fafafa'
                            }}>
                              <div style={{ marginBottom: '1rem' }}>
                              <ui5-icon name="folder-full" style={{ width: '3rem', height: '3rem', color: 'orange'}}></ui5-icon>
                              </div>
                              <Text style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                                {autoT('Arrastra y suelta tu archivo aquí')}
                              </Text>
                              <Text style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                                {autoT('o selecciona un archivo')}
                              </Text>
                              <div style={{ 
                                backgroundColor: '#eff6ff', 
                                padding: '0.5rem', 
                                borderRadius: '4px', 
                                marginBottom: '1rem',
                                border: '1px solid #3b82f6'
                              }}>
                                <Text style={{ fontSize: '12px', color: '#1d4ed8' }}>
                                  {autoT('Formatos soportados: PDF, DOC, XLS, JPG, PNG (Máx. 10MB)')}
                                </Text>
                              </div>
                              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                <Button design="Transparent">{autoT('Cancelar')}</Button>
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                  onChange={async (e) => {
                                    if (e.target.files[0]) {
                                      await handleAttachmentUpload('dispatchGuide', e.target.files[0]);
                                    }
                                  }}
                                  style={{ display: 'none' }}
                                  id="dispatchGuide-upload"
                                />
                                <Button 
                                  design="Emphasized"
                                  onClick={() => document.getElementById('dispatchGuide-upload').click()}
                                >
                                  {autoT('Seleccionar archivo')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Otros */}
                    <div style={{ 
                      border: '1px solid #3b82f6', 
                      borderRadius: '8px', 
                      backgroundColor: 'white',
                      overflow: 'hidden'
                    }}>
                      <div 
                        onClick={() => toggleAttachmentSection('other')}
                        style={{ 
                          padding: '1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: '#eff6ff'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Text style={{ fontSize: '16px', fontWeight: 'bold' }}> <ui5-icon name="attachment"></ui5-icon> {autoT('Otros')}</Text>
                          <div style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {autoT('Opcional')}
                          </div>
                        </div>
                        <Text>{attachmentSections.other?.isOpen ? '▲' : '▼'}</Text>
                      </div>
                      
                      {attachmentSections.other?.isOpen && (
                        <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                          {attachments.other ? (
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '1rem',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #10b981',
                              borderRadius: '6px'
                            }}>
                              <div>
                                <Text style={{ fontWeight: 'bold', color: '#059669' }}> <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> {autoT('Archivo adjuntado')}</Text>
                                <Text style={{ fontSize: '14px', color: '#6b7280' }}>{attachments.other.name || attachments.other.file?.name}</Text>
                              </div>
                              <Button 
                                design="Transparent" 
                                icon="delete"
                                onClick={() => removeAttachment('other')}
                                style={{ color: '#ef4444' }}
                              >
                                {autoT('Remover')}
                              </Button>
                            </div>
                          ) : (
                            <div style={{
                              border: '2px dashed #d1d5db',
                              borderRadius: '8px',
                              padding: '2rem',
                              textAlign: 'center',
                              backgroundColor: '#fafafa'
                            }}>
                              <div style={{ marginBottom: '1rem' }}>
                              <ui5-icon name="folder-full" style={{ width: '3rem', height: '3rem', color: 'orange'}}></ui5-icon>
                              </div>
                              <Text style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                                {autoT('Arrastra y suelta tu archivo aquí')}
                              </Text>
                              <Text style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                                {autoT('o selecciona un archivo')}
                              </Text>
                              <div style={{ 
                                backgroundColor: '#eff6ff', 
                                padding: '0.5rem', 
                                borderRadius: '4px', 
                                marginBottom: '1rem',
                                border: '1px solid #3b82f6'
                              }}>
                                <Text style={{ fontSize: '12px', color: '#1d4ed8' }}>
                                  {autoT('Formatos soportados: PDF, DOC, XLS, JPG, PNG (Máx. 10MB)')}
                                </Text>
                              </div>
                              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                <Button design="Transparent">{autoT('Cancelar')}</Button>
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                  onChange={async (e) => {
                                    if (e.target.files[0]) {
                                      await handleAttachmentUpload('other', e.target.files[0]);
                                    }
                                  }}
                                  style={{ display: 'none' }}
                                  id="other-upload"
                                />
                                <Button 
                                  design="Emphasized"
                                  onClick={() => document.getElementById('other-upload').click()}
                                >
                                  {autoT('Seleccionar archivo')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
                
                <div style={{ 
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'white',
                  padding: '1rem',
                  marginTop: '2rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                  borderTop: '1px solid #e5e5e5'
                }}>
                  <Button 
                    onClick={() => setSelectedStep(2)}
                    icon="arrow-left"
                  >
                    {autoT('Anterior')}
                  </Button>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <Button 
                      design="Transparent"
                      onClick={() => setShowDraftDialog(true)}
                      icon="save"
                      disabled={!isFirstStepComplete() || isSaving}
                    >
                      {autoT('Guardar Borrador')}
                    </Button>
                    <Button 
                      design="Positive"
                      onClick={() => setShowConfirmDialog(true)}
                      icon={isSaving ? "loading" : "save"}
                      disabled={isSaving}
                    >
                      {uploadProgress.isUploading 
                        ? `${autoT('Espere, cargando')} ${uploadProgress.current} ${autoT('de')} ${uploadProgress.total} ${autoT('archivos...')}`
                        : isSaving 
                          ? autoT('Guardando...') 
                          : autoT('Guardar Notificación')
                      }
                    </Button>
                  </div>
                </div>
              </WizardStep>
            </Wizard>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación */}
      {showConfirmDialog && (
        <Dialog
          open={showConfirmDialog}
          headerText={autoT('Confirmar Guardar Notificación')}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.5rem' }}>
              <Button 
                onClick={() => setShowConfirmDialog(false)}
              >
                {autoT('Cancelar')}
              </Button>
              <Button 
                design="Emphasized"
                onClick={() => {
                  setShowConfirmDialog(false);
                  handleSubmit();
                }}
              >
                {autoT('Confirmar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            ¿Está seguro que desea guardar la notificación?
          </div>
        </Dialog>
      )}

      {/* Modal de Confirmación de Borrador */}
      {showDraftDialog && (
        <Dialog
          open={showDraftDialog}
          headerText={autoT('Confirmar Guardar Borrador')}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.5rem' }}>
              <Button 
                onClick={() => setShowDraftDialog(false)}
              >
                {autoT('Cancelar')}
              </Button>
              <Button 
                design="Emphasized"
                onClick={() => {
                  setShowDraftDialog(false);
                  handleSaveDraft();
                }}
              >
                {autoT('Confirmar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            ¿Está seguro que desea guardar el borrador? Solo se guardará la información de la primera parte.
          </div>
        </Dialog>
      )}

      {/* Modal de Éxito */}
      {showSuccessDialog && (
        <Dialog
          open={showSuccessDialog}
          headerText={autoT('Operación Exitosa')}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
              <Button 
                design="Emphasized"
                onClick={handleSuccessDialogClose} // Usar la nueva función
              >
                {autoT('Aceptar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            Notificación Numero : {asnNumber} creada correctamente
          </div>
        </Dialog>
      )}

      {/* Modal de Éxito de Borrador */}
      {showDraftSuccessDialog && (
        <Dialog
          open={showDraftSuccessDialog}
          headerText={autoT('Borrador Guardado')}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
              <Button 
                design="Emphasized"
                onClick={() => {
                  setShowDraftSuccessDialog(false);
                  navigate('/home', { state: { activePart: 'home', selectedTab: 2 } });
                }}
              >
                {autoT('Aceptar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            Borrador guardado correctamente con número: {asnNumber}
          </div>
        </Dialog>
      )}

     {/* Modal de redireccion */}
      {showViewAsnDialog && (
        <Dialog
          open={showViewAsnDialog}
          headerText={autoT('Desea visualizar notificación')}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.5rem' }}>
              <Button 
                onClick={() => {
                  setShowViewAsnDialog(false);
                  navigate('/home', { state: { activePart: 'home', selectedTab: 2 } }); // Lista de Notificaciones
                }}
              >
                {autoT('No')}
              </Button>
              <Button 
                design="Emphasized"
                onClick={() => {
                  setShowViewAsnDialog(false);
                  navigate(`/asn/${asnDocumentId}`); // Redirigir al detalle de la Notificación
                }}
              >
                {autoT('Sí')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            ¿Desea visualizar la notificación?
          </div>
        </Dialog>
      )}
      {/* Modal de Error */}
      {showErrorDialog && (
        <Dialog
          open={showErrorDialog}
          headerText={autoT('Error')}
          state="Error"
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
              <Button 
                onClick={() => setShowErrorDialog(false)}
                style={{ backgroundColor: 'red', color: 'white' }}
              >
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            {errorMessage}
          </div>
        </Dialog>
      )}

      {/* Modal de Error de Fecha */}
      {showDateErrorDialog && (
        <Dialog
          open={showDateErrorDialog}
          headerText={autoT('Error en la fecha')}
          state="Error"
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
              <Button 
                onClick={() => setShowDateErrorDialog(false)}
                style={{ backgroundColor: 'red', color: 'white' }}
              >
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            {dateErrorMessage}
          </div>
        </Dialog>
      )}

      {/* Modal de Error de Cantidad */}
      {showQuantityErrorDialog && (
        <Dialog
          open={showQuantityErrorDialog}
          headerText={autoT('Error en cantidad')}
          state="Error"
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
              <Button 
                onClick={() => setShowQuantityErrorDialog(false)}
                style={{ backgroundColor: 'red', color: 'white' }}
              >
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            {quantityErrorMessage}
          </div>
        </Dialog>
      )}

      {/* Agregar el Dialog para errores de bultos */}
      {showBultoErrorDialog && (
        <Dialog
          open={showBultoErrorDialog}
          headerText={autoT('Error en totales de bultos')}
          state="Error"
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem' }}>
              <Button 
                onClick={() => setShowBultoErrorDialog(false)}
                style={{ backgroundColor: 'red', color: 'white' }}
              >
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            {bultoErrorMessage}
          </div>
        </Dialog>
      )}

      {/* Modal de Creando Notificación */}
      {showCreatingDialog && (
        <Dialog
          open={showCreatingDialog}
          headerText={autoT('Procesando')}
          preventInitialFocus={true}
          style={{ 
            minWidth: '400px',
            textAlign: 'center'
          }}
        >
          <div style={{ 
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <BusyIndicator active="true" size="L" />
            <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {autoT('Espere, Creando Notificación...')}
            </Text>
            {uploadProgress.isUploading && (
              <Text style={{ fontSize: '14px', color: '#666' }}>
                {autoT('Cargando')} {uploadProgress.current} {autoT('de')} {uploadProgress.total} {autoT('archivos')}
              </Text>
            )}
          </div>
        </Dialog>
      )}
      {showInactivityWarning && (
        <Dialog
          open={showInactivityWarning}
          headerText={autoT('Inactividad detectada')}
          style={{ minWidth: '350px', textAlign: 'center' }}
          preventInitialFocus={true}
        >
          <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {autoT('Has estado inactivo. Serás redirigido en')} {inactivityCountdown} {autoT('segundos.')}
            </Text>
            <Button
              design="Emphasized"
              onClick={() => {
                setShowInactivityWarning(false);
                setInactivityCountdown(10);
                // Reiniciar timers de inactividad
                clearInterval(inactivityWarningTimer.current);
                clearTimeout(inactivityMainTimer.current);
                // Volver a iniciar el ciclo de inactividad
                // (esto se logra disparando un evento de actividad)
                window.dispatchEvent(new Event('mousemove'));
              }}
            >
              {autoT('Seguir aquí')}
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default CreateASN;